import Janus from '../external/janus.es'
import { Machine, assign, actions } from 'xstate';
const { escalate, sendParent, send, forwardTo, cancel, raise, choose } = actions;
import { CallStatusErrorCode } from './errorCodes'
import enableScreenRecording from './janussession';

let defaultMediaConstraints = undefined;

const isElectron =  navigator.userAgent.indexOf('Electron') !== -1

// call events:
// - incomingcall(incoming)
// - declining (incoming)
// - declined (incoming)
// - accepting (incoming)
// - accepted (incoming, outgoing)
// - hangup (midcall, incoming, outgoing)
//
// - calling (outgoing)
// - proceeding (outgoing)
// - ringing (outgoing)
// - progress (outgoing)
// - accepted (incoming, outgoing)
// - hangingup (midcall, outgoing)
// - hangup (midcall, incoming, outgoing)
//
// - updating (midcall)
// - updated (midcall)
// - transferring (midcall)
// - transfer (midcall) // incoming REFER request
// - holding (midcall)
// - resuming (midcall)
// - hangingup (midcall)
// - dtmfsent (midcall) // SIP INFO method only
// - updatingcall (midcall)
// - hangingup (midcall, outgoing)
// - hangup (midcall, outgoing)
//
//

export const callStateMachine = Machine({
    id: 'ipfxCallStateMachine',
    initial: 'idle',
    context: {
        sip_call_id: null,
        call_id: null,
        remote_uri: null,
        pluginHandle: null,
        opaqueId: null,
        instanceId: null,
        queuedHangup: false,
        makeCallTargetUri: null,
        incomingCallJsep: null,
        doVideoProcessed: false,
        doVideo: false,                     // requested
        videoSource: undefined,             // requested
        videoSourceProcessed: undefined,    // requested
        enabledVideoSource: undefined,      // actual
        isVideoProcessingComplete: true,        
        preferSendVideo: false,
        preferRecvVideo: false,
        preferSendAudio: false,
        preferRecvAudio: false,
        canSendVideo: false,
        canRecvVideo: false,
        canSendAudio: false,
        canRecvAudio: false,
        isHolding: false,
        isLastXferBlind: false,
        isLastXferSuper: false,
        incomingCallUserName: undefined,
        incomingCallDisplayName: undefined,
        isCallee: false,
        retry491Count: 0,
        isIceRestartRequired: false,
        srRequested: false,
        srCallId: undefined,
        srFrameRate: undefined,
        srScreenWidth: undefined,
        srScreenHeight: undefined,
        optionCaptureOnDemand: undefined,
        optionCaptureOnDemandTag: undefined,
        optionRecordAudio: undefined,
        srTarget: undefined,
        srRetryCount: 0,
    },
    on: {
        DO_START_SR: {
            actions:
                assign({
                    srRequested: () => true, 
                    srTarget: (context, event) => event.target, 
                    srCallId: (context, event) => event.callId, 
                    srRetryCount: () => 0, 
                    srFrameRate: (context, event) => event.frameRate,
                    srScreenWidth: (context, event) => event.screenWidth,
                    srScreenHeight: (context, event) => event.screenHeight,
                    optionCaptureOnDemand: (context, event) => event.optionCaptureOnDemand,
                    optionCaptureOnDemandTag: (context, event) => event.optionCaptureOnDemandTag,
                    optionRecordAudio: (context, event) => event.optionRecordAudio,
                })
        },
        DO_STOP_SR: {
            actions: [
                assign({srRequested: () => false, srRetryCount: () => 0, optionCaptureOnDemand: () => undefined,  optionCaptureOnDemandTag: () => undefined, optionRecordAudio: () => undefined }),
                send('DO_HANGUP'),                 
            ]
        },
        DO_ENABLE_VIDEO: {
            actions: assign({
                doVideo: (context, event) => event.doVideo,
                videoSource: (context, event) => event.videoSource,
            })
        },
        RESTART_ADD_CAPTURE_ON_DEMAND_OPTION_SR:  {
            actions: [
                assign({
                    srRequested: () => true,
                    srRetryCount: () => 0,
                    srCallId: (context, event) => event.newCallId,
                    optionCaptureOnDemand: (context, event) => event.optionCaptureOnDemand,
                    optionCaptureOnDemandTag: (context, event) => event.optionCaptureOnDemandTag,
                    optionRecordAudio: (context, event) => event.optionRecordAudio,
                }),
                send('DO_HANGUP')
            ]
        },
        RESTART_REMOVE_CAPTURE_ON_DEMAND_OPTION_SR:  {
            actions: [
                assign({
                    srRequested: () => true,
                    srRetryCount: () => 0,
                    srCallId: (context, event) => event.newCallId,
                    optionCaptureOnDemand: () => 0,
                    optionCaptureOnDemandTag: () => undefined,
                    optionRecordAudio: () => undefined
                }),
                send('DO_HANGUP')
            ]
        },
        DO_POST_ENABLE_VIDEO : {
            // this will trigger media update when connected to get the video going
            actions: assign({doVideoProcessed: (context, event) => false }),
        },
        DO_REJECT_REFER: { actions: 'declineTransferAction' },
        DO_BLIND_XFER: { actions: sendParent((context, error) => ({ type: 'DO_BLIND_XFER_FAILED', instanceId: context.instanceId, message: 'Call state incompatible with transfer request' })) },
        DO_XFER: { actions: sendParent((context, error) => ({ type: 'DO_XFER_FAILED', instanceId: context.instanceId, message: 'Call state incompatible with transfer request' })) },
        TRANSFERRING: { actions: sendParent((context, error) => ({ type: 'CALL_STATE_DO_XFERRING', instanceId: context.instanceId })) },
        JANUS_SESSION_ENDED: {
            target: 'finalizing',
            actions: [
                'logJanusSessionEnded',
                'doHangupAction',
                sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: 0, reason: 'End of session' })),
                sendParent((context, event) => ({ type: 'CALL_STATE_DEEP_IDLE', instanceId: context.instanceId }))
            ]
        },
    },
    states: {
        finalizing: {
            after: { 3000: { target: 'finalized' } },
            on : {
                CLEANUP: { target: 'finalized' },
            }
        },
        finalized: {
            type: 'final'
        },
        idle: {
            entry: [
                sendParent((context, event) => ({ type: 'CALL_STATE_DEEP_IDLE', instanceId: context.instanceId }), { delay: 2000, id: 'DEEP_IDLE_TIMER' }),
                choose([
                {
                    // when DO_START_SR while hanging up existing SR call
                    cond: (context) => context.srRequested && context.srRetryCount == 0,
                    actions: send((context, event) => ({ type: 'DO_START_SR_IF', instanceId: context.instanceId }), { delay: 250, id: 'START_SR_TIMER' })
                },
                {
                        // when retrying on repeated failure
                        cond: (context) => context.srRequested && context.srRetryCount > 0,
                        actions: send((context, event) => ({ type: 'DO_START_SR_IF', instanceId: context.instanceId }), { delay: 15000, id: 'START_SR_TIMER' })
                }]),
                assign({ sip_call_id: () => null }),
                assign({ call_id: () => null }),
                assign({ remote_uri: () => null }),
                assign({ queuedHangup: () => false }),
                assign({ makeCallTargetUri: () => null }),
                assign({ incomingCallJsep: () => null }),
                assign({ doVideoProcessed: context => context.doVideo }),
                assign({ enabledVideo: () => false }),
                assign({ videoSourceProcessed: context => context.videoSource }),
                assign({ enabledVideoSource: () => undefined }),
                assign({ isVideoProcessingComplete: () => true }),
                assign({ preferSendVideo: () => false }),
                assign({ preferRecvVideo: () => false }),
                assign({ preferSendAudio: () => false }),
                assign({ preferRecvAudio: () => false }),
                assign({ canSendVideo: () => false }),
                assign({ canRecvVideo: () => false }),
                assign({ canSendAudio: () => false }),
                assign({ canRecvAudio: () => false }),
                assign({ isHolding: () => false }),
                assign({ isLastXferBlind: () => false }),
                assign({ isLastXferSuper: () => false }),
                assign({ incomingCallUserName: () => undefined }),
                assign({ incomingCallDisplayName: () => undefined }),
                assign({ isCallee: () => false }),
                assign({ retry491Count: () => 0 }),
                assign({ isIceRestartRequired: () => false }),
            ],
            exit: [cancel('DEEP_IDLE_TIMER'), cancel('START_SR_TIMER')],
            on: {
                DO_START_SR: {
                    actions: [
                        assign({
                            srRequested: () => true, 
                            srTarget: (context, event) => event.target, 
                            srCallId: (context, event) => event.callId, 
                            srRetryCount: () => 0, 
                            srFrameRate: (context, event) => event.frameRate,
                            srScreenWidth: (context, event) => event.screenWidth,
                            srScreenHeight: (context, event) => event.screenHeight,
                            optionCaptureOnDemand: (context, event) => event.optionCaptureOnDemand,
                            optionCaptureOnDemandTag: (context, event) => event.optionCaptureOnDemandTag,   
                            optionRecordAudio: (context, event) => event.optionRecordAudio,     
                        }),
                        send((context, event) => ({ type: 'DO_MAKE_CALL', instanceId: event.instanceId, target: event.target, callId:  event.callId }))
                    ]
                },                
                DO_START_SR_IF: { actions: [
                    choose([
                        {
                            cond: (context) => context.srRequested && ++context.srRetryCount <= 1,
                            actions: send((context, event) => ({ type: 'DO_MAKE_CALL', instanceId: context.instanceId, target: context.srTarget, callId: context.srCallId }))
                        }
                    ]),
                    'logContext'
                ]},
                RESTART_ADD_CAPTURE_ON_DEMAND_OPTION_SR:  {
                    actions: [
                        assign({
                            srRequested: () => true,
                            srRetryCount: () => 0,
                            srCallId: (context, event) => event.newCallId,
                            optionCaptureOnDemand: (context, event) => event.optionCaptureOnDemand,
                            optionCaptureOnDemandTag: (context, event) => event.optionCaptureOnDemandTag,
                            optionRecordAudio: (context, event) => event.optionRecordAudio,
                        }),
                        send((context, event) => ({ type: 'DO_MAKE_CALL', instanceId: event.instanceId, target: event.target, callId:  event.newCallId }))
                    ]
                },
                RESTART_REMOVE_CAPTURE_ON_DEMAND_OPTION_SR:  {
                    actions: [
                        assign({
                            srRequested: () => true,
                            srRetryCount: () => 0,
                            srCallId: (context, event) => event.newCallId,
                            optionCaptureOnDemand: () => 0,
                            optionCaptureOnDemandTag: () => undefined,
                            optionRecordAudio: () => undefined,
                        }),
                        send((context, event) => ({ type: 'DO_MAKE_CALL', instanceId: event.instanceId, target: event.target, callId:  event.newCallId }))
                    ]
                },
        
                DO_MAKE_CALL: {
                    target: 'tryingMakeCall'
                },
                INCOMINGCALL: {
                    // note Janus doesn't provide headers with replacing invite - must fix in Janus
                    target: 'handlingIncomingCall',
                    actions: assign({
                        isCallee: () => true,
                        sip_call_id: (context, event) => event.call_id,
                        call_id: (context, event) => event.result["headers"] === undefined ? event.call_id : event.result["headers"]["Push-call-id"],
                        remote_uri: (context, event) => event.result["username"]
                    })
                },
                CALLING: { actions: 'doHangupAction' },
                RINGING: { actions: 'doHangupAction' },
                PROCEEDING: { actions: 'doHangupAction' },
                PROGRESS: { actions: 'doHangupAction' },
                ACCEPTED: { actions: 'doHangupAction' },
                '*': undefined
            }
        },
        hangingup: {
            on: {
                CLEANUP: {
                    target: 'idle',
                    actions: sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: 0, reason: 'Normal clearing' }))
                },
                HANGUP: {
                    cond: (context, event) => context.sip_call_id !== null && event["call_id"] === context.sip_call_id,
                    target: 'idle',
                    actions: sendParent((context, event) => ({ 
                        type: 'CALL_STATE_IDLE', 
                        instanceId: context.instanceId, 
                        code: event.result["code"], 
                        reason: event.result["reason"],
                        reason_header_protocol: event.result["reason_header_protocol"],
                        reason_header_cause: event.result["reason_header_cause"]
                    }))
                },
                DECLINED: {
                    target: 'idle',
                    actions: sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: event.result["code"], reason: event.result["reason"] }))
                },
            },
            // we have a problem with SIP plugin that doesn't handle crosstalk,
            // until it's fixed we handle it ourselves
            '*': {
                target: undefined,
                actions: () => Janus.warn("!!! Stuck in hangingup state, hangup from the other end !!!")
            },
        },
        handlingIncomingCall: {
            invoke: {
                id: 'handleIncomingCall',
                src: 'handleIncomingCallService',
                onError: {
                    target: 'idle',
                    actions: [
                        'doHangupAction',
                        escalate((context, event) => ({ reason: event.type, message: event.data.message }))
                    ]
                }
            },
            entry: [
                assign({
                    doVideoProcessed: context => context.doVideo,
                    videoSourceProcessed: context => context.videoSource,
                    incomingCallJsep: (context, event) => event.jsep,
                    call_id: (context, event) => context.call_id,
                    incomingCallUserName: (context, event) => calculateUserName(context, event),
                    incomingCallDisplayName: (context, event) => calculateDisplayName(context, event)
                }),
                'logEvent',
                sendParent((context, event) => ({
                    // note Janus doesn't provide headers with replacing invite - must fix in Janus
                    type: 'CALL_STATE_INCOMING_CALL',
                    instanceId: context.instanceId,
                    callerUsername: calculateUserName(context, event),
                    callerDisplay: calculateDisplayName(context, event),
                    callId: event.result["headers"] === undefined ? event.call_id : event.result["headers"]["Push-call-id"],
                    replaces: event.result["replaces"], // this is sip_call_id (not call_id)
                    referredByData: calculateReferredByData(event)
                })),
                'messageAckIncomingCallAction'
            ],
            exit: assign({ incomingCallJsep: () => null }),
            on: {
                DO_ACCEPT: { actions: forwardTo('handleIncomingCall') },
                DO_DECLINE: { actions: forwardTo('handleIncomingCall') },
                DO_HANGUP: { actions: 'doHangupAction' },
                DO_ACCEPT_SUCCEEDED: sendParent((context, event) => ({ type: 'CALL_STATE_CONNECTING', instanceId: context.instanceId })),
                DO_ACCEPT_FAILED: { 
                    actions: [
                        sendParent((context, event) => ({ 
                            type: 'CALL_STATE_ERROR', 
                            instanceId: context.instanceId, 
                            errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                CallStatusErrorCode.ERROR_ANSWER_CALL_PERMISSION : CallStatusErrorCode.ERROR_ANSWER_CALL,
                            errorMessage: 'Failed to answer an incoming call: ' + GetErrorMessage(event) 
                        })),
                        'doHangupAction'
                    ] 
                },
                DO_DECLINE_SUCCEEDED: undefined,
                DO_DECLINE_FAILED: { 
                    actions: [
                        sendParent((context, event) => ({ 
                            type: 'CALL_STATE_ERROR', 
                            instanceId: context.instanceId, 
                            errorCode: CallStatusErrorCode.ERROR_HANG_UP, 
                            errorMessage: 'Failed to decline an incoming call: ' +  event.message })),
                        'doHangupAction'
                    ] 
                },
                DECLINING: {
                    target: 'hangingup',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_HANGINGUP',
                        instanceId: context.instanceId
                    }))
                },
                DECLINED: {
                    target: 'idle',
                    actions: sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: event.result["code"], reason: event.result["reason"] }))
                },
                ACCEPTING: {},
                ACCEPTED: {
                    target: 'connected',
                    actions: sendParent(
                        (context, event) => ({
                            type: 'CALL_STATE_CONNECTED',
                            instanceId: context.instanceId,
                            isInitial: true,
                            remoteUri: calculateUserName(context, event),
                            remoteDisplay: calculateDisplayName(context, event),
                            hasLocalAudio: context.canSendAudio,
                            hasRemoteAudio: context.canRecvAudio,
                            hasLocalVideo: context.canSendVideo,
                            hasRemoteVideo: context.canRecvVideo,
                            localVideoSource: context.enabledVideoSource
                        }))
                },
                HANGUP: {
                    cond: (context, event) => context.sip_call_id !== null && event["call_id"] === context.sip_call_id,
                    target: 'idle',
                    actions: sendParent((context, event) => ({ 
                        type: 'CALL_STATE_IDLE', 
                        instanceId: context.instanceId, 
                        code: event.result["code"], 
                        reason: event.result["reason"],
                        reason_header_protocol: event.result["reason_header_protocol"],
                        reason_header_cause: event.result["reason_header_cause"]
                    }))
                },
                HANGINGUP: {
                    target: 'hangingup',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_HANGINGUP',
                        instanceId: context.instanceId
                    }))
                },
            }
        },
        tryingMakeCallTimeout: {
            always: {
                target: 'idle',
                actions: [
                    'doHangupAction',
                    escalate((context, error) => ({
                        reason: 'DO_MAKE_CALL_FAILED',
                        instanceId: context.instanceId,
                        message: "Timeout while trying to make call"
                    })),
                    sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: 408, reason: "Request Timeout" }))
                ]                
            }
        },
        callingOutTimeout: {
            entry: [
                'doHangupAction',
                sendParent((context, event) => ({
                    type: 'CALL_STATE_TIMEOUT',
                    instanceId: context.instanceId,
                    message: "Timeout while calling out"
                }))
            ],
            on: {
                CLEANUP: {
                    target: 'idle',
                    actions: sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: 408, reason: 'Request Timeout' }))
                },
                HANGUP: {
                    cond: (context, event) => context.sip_call_id !== null && event["call_id"] === context.sip_call_id,
                    target: 'idle',
                    actions: sendParent((context, event) => ({ 
                        type: 'CALL_STATE_IDLE', 
                        instanceId: context.instanceId, 
                        code: event.result["code"], 
                        reason: event.result["reason"],
                        reason_header_protocol: event.result["reason_header_protocol"],
                        reason_header_cause: event.result["reason_header_cause"]
                    }))
                },
                '*': undefined
            }
        },
        tryingMakeCall: {
            invoke: {
                id: 'makeCall',
                src: 'makeCallService',
                onError: {
                    target: 'idle',
                    actions: [
                        'doHangupAction',
                        escalate((context, event) => ({ reason: 'DO_MAKE_CALL_FAILED', message: event.data.message }))
                    ]
                }
            },
            after: { 15000: 'tryingMakeCallTimeout' },
            entry: [
                assign({ doVideoProcessed: context => context.doVideo, videoSourceProcessed: context => context.videoSource }),
                assign({ remote_uri: (context, event) => event.target }),
                sendParent((context, event) => ({ type: 'CALL_STATE_TRYING_OUT', instanceId: context.instanceId, remoteUri: event.target }))
            ],
            on: {
                DO_HANGUP: { actions: 'doHangupAction' },
                DO_MAKE_CALL_SUCCEEDED: [{
                    actions: 'doHangupAction', cond: (context) => context.queuedHangup
                }, {
                    actions: assign({
                        preferSendAudio: (context, event) => event.preferSendAudio,
                        preferRecvAudio: (context, event) => event.preferRecvAudio,
                        preferSendVideo: (context, event) => event.preferSendVideo,
                        preferRecvVideo: (context, event) => event.preferRecvVideo,
                    })
                }],
                DO_MAKE_CALL_FAILED: {
                    target: 'idle',
                    actions: [
                        sendParent((context, event) => ({ 
                            type: 'CALL_STATE_ERROR', 
                            instanceId: context.instanceId, 
                            errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                CallStatusErrorCode.ERROR_MAKE_CALL_PERMISSION : CallStatusErrorCode.ERROR_MAKE_CALL,
                            errorMessage: 'Unable to dial: ' + GetErrorMessage(event) })),
                        'doHangupAction',
                        choose([
                            {
                                cond: (context, event) => context.srRequested && GetErrorMessage(event).toLowerCase().includes('permission'),
                                actions: [
                                    assign({
                                        srRetryCount: () => 1000,
                                    }),
                                ],
                            }        
                        ])                        
                        //sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: 403, reason: 'Permission denied' }))
                    ]
                },
                CALLING: {
                    target: 'ringingOut',
                    actions: [
                        assign({
                            call_id: (context, event) => event.call_id,
                            sip_call_id: (context, event) => event.call_id,
                        }),
                        sendParent((context, event) => ({
                            type: 'CALL_STATE_CALLING_OUT',
                            instanceId: context.instanceId,
                            remoteUri: context.remote_uri,
                            callId: event.call_id
                        }))]
                },
                RINGING: {
                    target: 'ringingOut',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_RINGING_OUT',
                        instanceId: context.instanceId,
                        remoteDisplay: event.result["todisplayname"]
                    }))
                },
                PROCEEDING: [
                    {
                        cond: (context, event) => event.result["code"] === 183,
                        target: 'progressOut',
                        actions: sendParent((context, event) => ({
                            type: 'CALL_STATE_PROGRESS_OUT',
                            instanceId: context.instanceId,
                        }))
                    },
                    {
                        target: 'ringingOut',
                        actions: sendParent((context, event) => ({
                            type: 'CALL_STATE_PROCEEDING_OUT',
                            instanceId: context.instanceId
                        }))
                    }
                ],
                PROGRESS: {
                    target: 'progressOut',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_PROGRESS_OUT',
                        instanceId: context.instanceId,
                        remoteDisplay: event.result["todisplayname"]
                    }))
                },
                ACCEPTED: {
                    target: 'connected',
                    actions: sendParent(
                        (context, event) => ({
                            type: 'CALL_STATE_CONNECTED',
                            instanceId: context.instanceId,
                            isInitial: true,
                            remoteUri: event.result["username"] !== undefined ? event.result["username"] : context.remote_uri,
                            remoteDisplay: event.result["todisplayname"],
                            hasLocalAudio: context.canSendAudio,
                            hasRemoteAudio: context.canRecvAudio,
                            hasLocalVideo: context.canSendVideo,
                            hasRemoteVideo: context.canRecvVideo,
                            localVideoSource: context.enabledVideoSource
                        }))
                },
                HANGUP: {
                    cond: (context, event) => context.sip_call_id !== null && event["call_id"] === context.sip_call_id,
                    target: 'idle',
                    actions: [
                        sendParent((context, event) => ({ 
                            type: 'CALL_STATE_ERROR', 
                            instanceId: context.instanceId, 
                            errorCode: 
                                event.result["code"] === 404 ? CallStatusErrorCode.ERROR_MAKE_CALL_NOT_FOUND :
                                    event.result["code"] === 488 ? CallStatusErrorCode.ERROR_MAKE_CALL_MEDIA_REJECTED :
                                        event.result["code"] === 486 ? CallStatusErrorCode.ERROR_MAKE_CALL_BUSY :
                                            event.result["code"] === 487 ? CallStatusErrorCode.ERROR_MAKE_CALL_DECLINED :
                                                CallStatusErrorCode.ERROR_MAKE_CALL, 
                            errorMessage: 'Could not connect: ' + event.result["reason"] 
                        })),
                        sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: event.result["code"], reason: event.result["reason"] }))
                    ]
                },
                HANGINGUP: {
                    target: 'hangingup',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_HANGINGUP',
                        instanceId: context.instanceId
                    }))
                },
                SIP_ERROR: {
                    target: 'idle',
                    actions: [
                        'doHangupAction',
                        escalate((context, event) => ({
                            reason: 'DO_MAKE_CALL_FAILED',
                            instanceId: context.instanceId,
                            message: event.message
                        }))
                    ]
                },
            }
        },
        ringingOut: {
            after: { 60000: 'callingOutTimeout' },
            on: {
                DO_HANGUP: { actions: 'doHangupAction' },
                PROGRESS: {
                    target: 'progressOut',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_PROGRESS_OUT',
                        instanceId: context.instanceId
                    }))
                },
                ACCEPTED: {
                    target: 'connected',
                    actions: sendParent(
                        (context, event) => ({
                            type: 'CALL_STATE_CONNECTED',
                            instanceId: context.instanceId,
                            isInitial: true,
                            remoteUri: context.remote_uri,
                            remoteDisplay: event.result["todisplayname"],
                            hasLocalAudio: context.canSendAudio,
                            hasRemoteAudio: context.canRecvAudio,
                            hasLocalVideo: context.canSendVideo,
                            hasRemoteVideo: context.canRecvVideo,
                            localVideoSource: context.enabledVideoSource
                        }))
                },
                HANGUP: {
                    cond: (context, event) => context.sip_call_id !== null && event["call_id"] === context.sip_call_id,
                    target: 'idle',
                    actions: [
                        // exclude 487 for replacing INVITE completion
                        choose([
                            {
                                cond: (context, event) => event.result["code"] !== 487,
                                actions: [
                                    sendParent((context, event) => ({ 
                                        type: 'CALL_STATE_ERROR', 
                                        instanceId: context.instanceId, 
                                        errorCode: 
                                            event.result["code"] === 404 ? CallStatusErrorCode.ERROR_MAKE_CALL_NOT_FOUND :
                                                event.result["code"] === 488 ? CallStatusErrorCode.ERROR_MAKE_CALL_MEDIA_REJECTED :
                                                    event.result["code"] === 486 ? CallStatusErrorCode.ERROR_MAKE_CALL_BUSY :
                                                            CallStatusErrorCode.ERROR_MAKE_CALL, 
                                        errorMessage: 'Could not connect: ' + event.result["reason"] 
                                    }))
                                ],
                            }        
                        ]),
                        sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: event.result["code"], reason: event.result["reason"] }))
                    ]
                },
                HANGINGUP: {
                    target: 'hangingup',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_HANGINGUP',
                        instanceId: context.instanceId
                    }))
                },
                RINGING: {
                    target: undefined,
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_RINGING_OUT',
                        instanceId: context.instanceId,
                        remoteDisplay: event.result["todisplayname"]
                    }))
                },
                PROCEEDING: [
                    {
                        cond: (context, event) => event.result["code"] === 183,
                        target: 'progressOut',
                        actions: sendParent((context, event) => ({
                            type: 'CALL_STATE_PROGRESS_OUT',
                            instanceId: context.instanceId,
                        }))
                    },
                    {
                        target: undefined,
                        actions: sendParent((context, event) => ({
                            type: 'CALL_STATE_PROCEEDING_OUT',
                            instanceId: context.instanceId
                        }))
                    }
                ],
            }
        },
        progressOut: {
            invoke: {
                id: 'handleRemoteEarlyMedia',
                src: 'handleRemoteMediaService',
                onError: {
                    target: 'idle',
                    actions: [
                        'doHangupAction',
                        escalate((context, event) => ({
                            reason: 'REMOTE_EARLY_MEDIA_HANDLING_FAILED',
                            message: event.data.message
                        })),
                        sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: 488, reason: 'Media error' }))
                    ]
                }
            },
            after: { 60000: 'callingOutTimeout' },
            on: {
                DO_HANGUP: { actions: 'doHangupAction' },
                PROGRESS: {
                    target: undefined,
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_PROGRESS_OUT',
                        instanceId: context.instanceId
                    }))
                },
                ACCEPTED: {
                    target: 'connected',
                    actions: sendParent(
                        (context, event) => ({
                            type: 'CALL_STATE_CONNECTED',
                            instanceId: context.instanceId,
                            isInitial: true,
                            remoteUri: context.remote_uri,
                            remoteDisplay: event.result["todisplayname"],
                            hasLocalAudio: context.canSendAudio,
                            hasRemoteAudio: context.canRecvAudio,
                            hasLocalVideo: context.canSendVideo,
                            hasRemoteVideo: context.canRecvVideo,
                            localVideoSource: context.enabledVideoSource
                        }))
                },
                HANGUP: {
                    cond: (context, event) => context.sip_call_id !== null && event["call_id"] === context.sip_call_id,
                    target: 'idle',
                    actions: [
                        // exclude 487 for replacing INVITE completion
                        choose([
                            {
                                cond: (context, event) => event.result["code"] !== 487,
                                actions: [
                                    sendParent((context, event) => ({ 
                                        type: 'CALL_STATE_ERROR', 
                                        instanceId: context.instanceId, 
                                        errorCode: 
                                            event.result["code"] === 404 ? CallStatusErrorCode.ERROR_MAKE_CALL_NOT_FOUND :
                                                event.result["code"] === 488 ? CallStatusErrorCode.ERROR_MAKE_CALL_MEDIA_REJECTED :
                                                    event.result["code"] === 486 ? CallStatusErrorCode.ERROR_MAKE_CALL_BUSY :
                                                            CallStatusErrorCode.ERROR_MAKE_CALL, 
                                        errorMessage: 'Could not connect: ' + event.result["reason"] 
                                    }))
                                ],
                            }        
                        ]),
                        sendParent((context, event) => ({ type: 'CALL_STATE_IDLE', instanceId: context.instanceId, code: event.result["code"], reason: event.result["reason"] }))
                    ]
                },
                HANGINGUP: {
                    target: 'hangingup',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_HANGINGUP',
                        instanceId: context.instanceId
                    }))
                },
                RINGING: {
                    target: 'ringingOut',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_RINGING_OUT',
                        instanceId: context.instanceId,
                        remoteDisplay: event.result["todisplayname"]
                    }))
                },
                PROCEEDING: [
                    {
                        cond: (context, event) => event.result["code"] === 183,
                        target: 'progressOut',
                        actions: sendParent((context, event) => ({
                            type: 'CALL_STATE_PROGRESS_OUT',
                            instanceId: context.instanceId,
                        }))
                    },
                    {
                        target: 'ringingOut',
                        actions: sendParent((context, event) => ({
                            type: 'CALL_STATE_PROCEEDING_OUT',
                            instanceId: context.instanceId
                        }))
                    }
                ],
                REMOTE_SDP_HANDLING_SUCCEEDED: {
                    actions: assign({
                        isVideoProcessingComplete: () => true,
                        canSendAudio: (context, event) => event.canSendAudio,
                        canRecvAudio: (context, event) => event.canRecvAudio,
                        canSendVideo: (context, event) => event.canSendVideo,
                        canRecvVideo: (context, event) => event.canRecvVideo,
                        enabledVideoSource: (context, event) => event.canSendVideo && context.videoSourceProcessed,
                        isIceRestartRequired: context => false,
                    })
                },
                REMOTE_SDP_HANDLING_FAILED: { 
                    actions: [
                        sendParent((context, event) => ({ 
                            type: 'CALL_STATE_ERROR', 
                            instanceId: context.instanceId, 
                            errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                CallStatusErrorCode.ERROR_MAKE_CALL_PERMISSION : CallStatusErrorCode.ERROR_MAKE_CALL,
                            errorMessage: 'Unable to connect an outgoing call:' + GetErrorMessage(event) })),
                        'doHangupAction'
                    ]
                },
            }
        },
        connected: {
            initial: 'notHold',
            invoke: [
                {
                    id: 'handleRemoteMedia',
                    src: 'handleRemoteMediaService',
                    onError: {
                        target: 'idle',
                        actions: [
                            'doHangupAction',
                            escalate((context, event) => ({
                                reason: 'REMOTE_MEDIA_HANDLING_FAILED',
                                message: event.data.message
                            }))
                        ]
                    }
                },
                {
                    id: 'executeBlindXfer',
                    src: 'executeXferService',
                    onError: {
                        target: 'idle',
                        actions: sendParent((context, error) => ({ type: 'DO_BLIND_XFER_FAILED', instanceId: context.instanceId, message: error.data.message }))
                    }
                },
                {
                    id: 'executeXfer',
                    src: 'executeXferService',
                    onError: {
                        target: 'idle',
                        actions: sendParent((context, error) => ({ type: 'DO_XFER_FAILED', instanceId: context.instanceId, message: error.data.message }))
                    }
                },
            ],
            entry: assign({ 
                remote_uri: (context, event) => event.result["username"] !== undefined ? event.result["username"] : context.remote_uri,
                retry491Count: () => 0,
                isVideoProcessingComplete: () => false,
                isIceRestartRequired: () => false,
            }),
            on: {
                DO_ICE_RESTART: { actions: assign({ isIceRestartRequired: () => true })},
                DO_HANGUP: { actions: 'doHangupAction' },
                DO_SEND_DTMF: { actions: 'doSendDtmfAction' },
                HANGUP: {
                    cond: (context, event) => context.sip_call_id !== null && event["call_id"] === context.sip_call_id,
                    target: 'idle',
                    actions: sendParent((context, event) => ({ 
                        type: 'CALL_STATE_IDLE', 
                        instanceId: context.instanceId, 
                        code: event.result["code"], 
                        reason: event.result["reason"],
                        reason_header_protocol: event.result["reason_header_protocol"],
                        reason_header_cause: event.result["reason_header_cause"]
                    }))
                },
                HANGINGUP: {
                    target: 'hangingup',
                    actions: sendParent((context, event) => ({
                        type: 'CALL_STATE_HANGINGUP',
                        instanceId: context.instanceId
                    }))
                },
                UPDATINGCALL: { actions: [ 
                    assign({ isVideoProcessingComplete: () => false}),
                    send((context, event) => ({ type: 'UPDATINGCALL_WRAPPED', event: event, context: context }))
                ]},
                UPDATINGCALL_WRAPPED: { actions: forwardTo('handleRemoteMedia') },
                ACCEPTED: { actions: send((context, event) => ({ type: 'ACCEPTED_WRAPPED', event: event, context: context })) },
                ACCEPTED_WRAPPED: { actions: forwardTo('handleRemoteMedia') },
                UPDATING: { actions: (context) => { Janus.log("Context=", context); } },
                UPDATINGCALL_SUCCEEDED: {
                    actions: [
                        sendParent((context, event) => ({
                            type: 'CALL_STATE_CONNECTED',
                            instanceId: context.instanceId,
                            isInitial: false,
                            hasLocalAudio: event.canSendAudio,
                            hasRemoteAudio: event.canRecvAudio,
                            hasLocalVideo: event.canSendVideo,
                            hasRemoteVideo: event.canRecvVideo,
                            localVideoSource: event.enabledVideoSource
                        })),
                        assign({
                            preferSendAudio: (context, event) => event.preferSendAudio,
                            preferRecvAudio: (context, event) => event.preferRecvAudio,
                            preferSendVideo: (context, event) => event.preferSendVideo,
                            preferRecvVideo: (context, event) => event.preferRecvVideo,
                            canSendAudio: (context, event) => event.canSendAudio,
                            canRecvAudio: (context, event) => event.canRecvAudio,
                            canSendVideo: (context, event) => event.canSendVideo,
                            enabledVideoSource: (context, event) => event.canSendVideo && context.videoSourceProcessed
                        })
                    ]
                },
                UPDATINGCALL_FAILED: { 
                    actions: [
                        sendParent((context, event) => ({ 
                            type: 'CALL_STATE_ERROR', 
                            instanceId: context.instanceId, 
                            errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                CallStatusErrorCode.ERROR_MIDCALL_PERMISSION : CallStatusErrorCode.ERROR_MIDCALL,
                            errorMessage: 'Mid-call signalling failure:' + GetErrorMessage(event) })),
                        'doHangupAction'
                    ]
                },
                REMOTE_SDP_HANDLING_SUCCEEDED: {
                    actions: [
                        sendParent((context, event) => ({
                            type: 'CALL_STATE_CONNECTED',
                            instanceId: context.instanceId,
                            isInitial: false,
                            hasLocalAudio: event.canSendAudio,
                            hasRemoteAudio: event.canRecvAudio,
                            hasLocalVideo: event.canSendVideo,
                            hasRemoteVideo: event.canRecvVideo,
                            localVideoSource: event.enabledVideoSource
                        })),
                        assign({
                            isVideoProcessingComplete: () => true,
                            canSendAudio: (context, event) => event.canSendAudio,
                            canRecvAudio: (context, event) => event.canRecvAudio,
                            canSendVideo: (context, event) => event.canSendVideo,
                            canRecvVideo: (context, event) => event.canRecvVideo,
                            enabledVideoSource: (context, event) => event.canSendVideo && context.videoSourceProcessed,
                            isIceRestartRequired: context => false,
                    })
                    ]
                },
                REMOTE_SDP_HANDLING_FAILED: { 
                    actions: [
                        sendParent((context, event) => ({ 
                            type: 'CALL_STATE_ERROR', 
                            instanceId: context.instanceId, 
                            errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                CallStatusErrorCode.ERROR_MIDCALL_PERMISSION : CallStatusErrorCode.ERROR_MIDCALL,
                            errorMessage: 'Mid-call signalling failure:' + GetErrorMessage(event) })),
                        'doHangupAction'
                    ]
                },
                TRANSFER: { actions: 'declineTransferAction' },
                NOTIFY: [
                    { actions: 'doHangupAction', cond: 'xferOk' },
                    { actions: sendParent((context, error) => ({ type: 'DO_BLIND_XFER_FAILED', instanceId: context.instanceId, message: 'Transfer failed' })), cond: 'blindXferFailed' },
                    { actions: sendParent((context, error) => ({ type: 'DO_XFER_FAILED', instanceId: context.instanceId, message: 'Transfer failed' })), cond: 'xferFailed' },
                    { target: undefined, cond: 'xferProgress' },
                ],
            },
            states: {
                notHold: {
                    entry:
                        choose([
                            {
                                cond: context => context.doVideo !== context.doVideoProcessed  || (context.doVideo && context.videoSource !== context.videoSourceProcessed),
                                actions: send('DO_CHECK_VIDEO'), 
                            },
                            {
                                cond: context => context.isIceRestartRequired,
                                actions: send('DO_ICE_RESTART'),
                            },
                        ]),
                    on: {
                        DO_CHECK_VIDEO: {
                            target: 'updatingVideo',
                            cond: context => context.doVideo !== context.doVideoProcessed  || (context.doVideo && context.videoSource !== context.videoSourceProcessed)
                        },
                        DO_ENABLE_VIDEO: {
                            target: 'updatingVideo',
                            actions: [assign({
                                doVideo: (context, event) => event.doVideo,
                                videoSource: (context, event) => event.videoSource,
                            })],
                            cond: (context, event) => !context.isVideoProcessingComplete || event.doVideo !== context.doVideoProcessed  || (event.doVideo && event.videoSource !== context.videoSourceProcessed)
                        },
                        DO_ICE_RESTART: 'restartingIce',
                        DO_HOLD: 'tryingHold',
                        DO_RESUME: undefined,
                        TRANSFER: {
                            actions: sendParent((context, event) => ({
                                type: 'HANDLE_REFER',
                                instanceId: context.instanceId,
                                refer_event: event
                            }))
                        }
                    }
                },
                restartingIce: {
                    invoke: {
                        id: 'restartIce',
                        src: 'restartIceService',
                        onError: {
                            target: 'notHold',
                            actions: sendParent((context, event) => ({
                                type: 'CALL_STATE_CONNECTED',
                                instanceId: context.instanceId,
                                isInitial: false,
                                hasLocalAudio: context.canSendAudio,
                                hasRemoteAudio: context.canRecvAudio,
                                hasLocalVideo: context.canSendVideo,
                                hasRemoteVideo: context.canRecvVideo,
                                localVideoSource: context.enabledVideoSource
                            }))
                        }
                    },
                    entry:[
                        assign({ isIceRestartRequired: () => false }),
                        assign ({retry491Count: (context, event) => event.type == 'RETRY_491' ? (context.retry491Count + 1):0}),
                        send((context, event) => ({ type: 'DO_HANGUP', reason: 'RESTARTING_ICE_TIMER' }), { delay: 16000, id: 'RESTARTING_ICE_TIMER' })
                    ],
                    exit: [cancel('RESTARTING_ICE_TIMER'), cancel('RETRY_491_TIMER'), assign({ isIceRestartRequired: () => false })],
                    on: {
                        RETRY_491: { target: 'restartingIce', internal: false },
                        // wrong state shall be treated as 491 invite error
                        SIP_ERROR: { actions: raise({ type: 'INVITE_ERROR', result: { code: 491} }), cond: (context, event) => event.error_code === 447},
                        INVITE_ERROR: [
                            // no more than 3 retries in a row
                            { actions: 'doHangupAction', cond: (context, event) => (event.result["code"] !== 491 || context.retry491Count >= 3) },
                            {
                                actions: [
                                    cancel('RESTARTING_ICE_TIMER'),
                                    send((context, event) => ({ type: 'RETRY_491' }),
                                    { delay: (context) => {
                                        let max = context.isCallee ? 600 : 300;
                                        let min = context.isCallee ? 310 : 0;
                                        let delay = 10 * Math.floor(Math.random() * (max - min + 1) + min)
                                        Janus.log("Delaying handling of INVITE_ERROR by " + delay + " ms");
                                        return delay;
                                    },
                                    id: 'RETRY_491_TIMER' })
                                ]
                            },
                        ],
                        DO_HOLD: undefined,
                        DO_RESUME: undefined,
                        DO_SEND_DTMF: undefined,
                        REMOTE_SDP_HANDLING_SUCCEEDED: {
                            target: 'notHold',
                            actions: [
                                sendParent((context, event) => ({
                                    type: 'CALL_STATE_CONNECTED',
                                    instanceId: context.instanceId,
                                    isInitial: false,
                                    hasLocalAudio: event.canSendAudio,
                                    hasRemoteAudio: event.canRecvAudio,
                                    hasLocalVideo: event.canSendVideo,
                                    hasRemoteVideo: event.canRecvVideo,
                                    localVideoSource: event.enabledVideoSource
                                })),
                                assign({
                                    isIceRestartRequired: context => false,
                                })
                            ]
                        },
                        ICE_RESTART_FAILED: {
                            target: 'notHold',
                            actions: [
                                sendParent((context, event) => ({
                                    type: 'CALL_STATE_ERROR',
                                    instanceId: context.instanceId,
                                    errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                        CallStatusErrorCode.ERROR_MIDCALL_PERMISSION : CallStatusErrorCode.ERROR_MIDCALL,
                                    errorMessage: 'Mid-call signalling failure:' + GetErrorMessage(event) 
                                })),
                                sendParent((context, event) => ({
                                    type: 'CALL_STATE_CONNECTED',
                                    instanceId: context.instanceId,
                                    isInitial: false,
                                    hasLocalAudio: context.canSendAudio,
                                    hasRemoteAudio: context.canRecvAudio,
                                    hasLocalVideo: context.canSendVideo,
                                    hasRemoteVideo: context.canRecvVideo,
                                    localVideoSource: context.enabledVideoSource
                                }))
                            ]
                        }
                    }                    
                },
                updatingVideo: {
                    invoke: {
                        id: 'updateMedia',
                        src: 'updateVideoService',
                        onError: {
                            target: 'notHold',
                            actions: sendParent((context, event) => ({
                                type: 'CALL_STATE_CONNECTED',
                                instanceId: context.instanceId,
                                isInitial: false,
                                hasLocalAudio: context.canSendAudio,
                                hasRemoteAudio: context.canRecvAudio,
                                hasLocalVideo: context.canSendVideo,
                                hasRemoteVideo: context.canRecvVideo,
                                localVideoSource: context.enabledVideoSource
                            }))
                        }
                    },
                    entry:[
                        assign({ isVideoProcessingComplete: () => false }),
                        assign({ doVideoProcessed: context => context.doVideo, videoSourceProcessed: context => context.videoSource, enabledVideoSource: () => undefined }),
                        assign ({retry491Count: (context, event) => event.type == 'RETRY_491' ? (context.retry491Count + 1):0}),
                        send((context, event) => ({ type: 'DO_HANGUP' }), { delay: 16000, id: 'UPDATING_VIDEO_TIMER' })
                    ],
                    exit: [cancel('UPDATING_VIDEO_TIMER'), cancel('RETRY_491_TIMER')],
                    on: {
                        RETRY_491: { target: 'updatingVideo', internal: false },
                        // wrong state shall be treated as 491 invite error
                        SIP_ERROR: { actions: raise({ type: 'INVITE_ERROR', result: { code: 491} }), cond: (context, event) => event.error_code === 447},
                        INVITE_ERROR: [
                            // no more than 3 retries in a row
                            { actions: 'doHangupAction', cond: (context, event) => (event.result["code"] !== 491 || context.retry491Count >= 3) },
                            {
                                actions: [
                                    cancel('UPDATING_VIDEO_TIMER'),
                                    send((context, event) => ({ type: 'RETRY_491' }),
                                    { delay: (context) => {
                                        let max = context.isCallee ? 600 : 300;
                                        let min = context.isCallee ? 310 : 0;
                                        let delay = 10 * Math.floor(Math.random() * (max - min + 1) + min)
                                        Janus.log("Delaying handling of INVITE_ERROR by " + delay + " ms");
                                        return delay;
                                    },
                                    id: 'RETRY_491_TIMER' })
                                ]
                            },
                        ],
                        DO_HOLD: undefined,
                        DO_RESUME: undefined,
                        DO_SEND_DTMF: undefined,
                        UPDATING_MEDIA: {
                            target: undefined,
                            actions: assign({
                                preferSendAudio: (context, event) => event.preferSendAudio,
                                preferRecvAudio: (context, event) => event.preferRecvAudio,
                                preferSendVideo: (context, event) => event.preferSendVideo,
                                preferRecvVideo: (context, event) => event.preferRecvVideo,
                            })
                        },
                        REMOTE_SDP_HANDLING_SUCCEEDED: {
                            target: 'notHold',
                            actions: [
                                sendParent((context, event) => ({
                                    type: 'CALL_STATE_CONNECTED',
                                    instanceId: context.instanceId,
                                    isInitial: false,
                                    hasLocalAudio: event.canSendAudio,
                                    hasRemoteAudio: event.canRecvAudio,
                                    hasLocalVideo: event.canSendVideo,
                                    hasRemoteVideo: event.canRecvVideo,
                                    localVideoSource: event.enabledVideoSource
                                })),
                                assign({
                                    isVideoProcessingComplete: () => true,
                                    canSendAudio: (context, event) => event.canSendAudio,
                                    canRecvAudio: (context, event) => event.canRecvAudio,
                                    canSendVideo: (context, event) => event.canSendVideo,
                                    canRecvVideo: (context, event) => event.canRecvVideo,
                                    enabledVideoSource: (context, event) => event.canSendVideo && context.videoSourceProcessed,
                                    isIceRestartRequired: context => false,
                                })
                            ]
                        },
                        UPDATE_MEDIA_FAILED: {
                            target: 'notHold',
                            actions: [
                                sendParent((context, event) => ({ 
                                    type: 'CALL_STATE_ERROR', 
                                    instanceId: context.instanceId, 
                                    errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                        CallStatusErrorCode.ERROR_MIDCALL_PERMISSION : CallStatusErrorCode.ERROR_MIDCALL,
                                    errorMessage: 'Mid-call signalling failure:' + GetErrorMessage(event) 
                                })),
                                sendParent((context, event) => ({
                                    type: 'CALL_STATE_CONNECTED',
                                    instanceId: context.instanceId,
                                    isInitial: false,
                                    hasLocalAudio: context.canSendAudio,
                                    hasRemoteAudio: context.canRecvAudio,
                                    hasLocalVideo: context.canSendVideo,
                                    hasRemoteVideo: context.canRecvVideo,
                                    localVideoSource: context.enabledVideoSource
                                }))
                            ]
                        }
                    }
                },
                tryingHold: {
                    invoke: {
                        id: 'holdCall',
                        src: 'holdCallService',
                        onError: {
                            target: 'notHold',
                            actions: sendParent((context, event) => ({
                                type: 'CALL_STATE_CONNECTED',
                                instanceId: context.instanceId,
                                isInitial: false,
                                hasLocalAudio: context.canSendAudio,
                                hasRemoteAudio: context.canRecvAudio,
                                hasLocalVideo: context.canSendVideo,
                                hasRemoteVideo: context.canRecvVideo,
                                localVideoSource: context.enabledVideoSource
                            }))
                        }
                    },
                    entry:[
                        assign ({retry491Count: (context, event) => event.type == 'RETRY_491' ? (context.retry491Count + 1):0}),
                        send((context, event) => ({ type: 'DO_HANGUP' }), { delay: 8000, id: 'TRYING_HOLD_TIMER' })
                    ],
                    exit: [cancel('TRYING_HOLD_TIMER'), cancel('RETRY_491_TIMER')],
                    on: {
                        RETRY_491: { target: 'tryingHold', internal: false },
                        // wrong state shall be treated as 491 invite error
                        SIP_ERROR: { actions: raise({ type: 'INVITE_ERROR', result: { code: 491} }), cond: (context, event) => event.error_code === 447},
                        INVITE_ERROR: [
                            // no more than 3 retries in a row
                            { actions: 'doHangupAction', cond: (context, event) => (event.result["code"] !== 491 || context.retry491Count >= 3) },
                            {
                                actions: [
                                    cancel('TRYING_HOLD_TIMER'),
                                    send((context, event) => ({ type: 'RETRY_491' }),
                                    { delay: (context) => {
                                        let max = context.isCallee ? 400 : 200;
                                        let min = context.isCallee ? 210 : 0;
                                        let delay = 10 * Math.floor(Math.random() * (max - min + 1) + min)
                                        Janus.log("Delaying handling of INVITE_ERROR by " + delay + " ms");
                                        return delay;
                                    },
                                    id: 'RETRY_491_TIMER' })
                                ]
                            },
                        ],
                        DO_HOLD: undefined,
                        DO_RESUME: undefined,
                        DO_SEND_DTMF: undefined,
                        HOLDING: {
                            target: undefined,
                            actions: assign({
                                preferSendAudio: (context, event) => event.preferSendAudio,
                                preferRecvAudio: (context, event) => event.preferRecvAudio,
                                preferSendVideo: (context, event) => event.preferSendVideo,
                                preferRecvVideo: (context, event) => event.preferRecvVideo,
                                canSendAudio: (context, event) => event.canSendAudio,
                                canRecvAudio: (context, event) => event.canRecvAudio,
                                canSendVideo: (context, event) => event.canSendVideo,
                                canRecvVideo: (context, event) => event.canRecvVideo,
                            })
                        },
                        REMOTE_SDP_HANDLING_SUCCEEDED: {
                            target: 'holding',
                            actions: [
                                assign({
                                isVideoProcessingComplete: () => true,
                                canSendAudio: (context, event) => event.canSendAudio,
                                canRecvAudio: (context, event) => event.canRecvAudio,
                                canSendVideo: (context, event) => event.canSendVideo,
                                canRecvVideo: (context, event) => event.canRecvVideo,
                                isIceRestartRequired: context => false,
                            })
                            ]
                        },
                        DO_HOLD_FAILED: {
                            target: 'notHold',
                            actions: [ 
                                sendParent((context, event) => ({ 
                                    type: 'CALL_STATE_ERROR', 
                                    instanceId: context.instanceId, 
                                    errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                        CallStatusErrorCode.ERROR_HOLD_PERMISSION : CallStatusErrorCode.ERROR_HOLD,
                                    errorMessage: 'Mid-call signalling failure:' + GetErrorMessage(event) })),
                                sendParent((context, event) => ({
                                    type: 'CALL_STATE_CONNECTED',
                                    instanceId: context.instanceId,
                                    isInitial: false,
                                    hasLocalAudio: context.canSendAudio,
                                    hasRemoteAudio: context.canRecvAudio,
                                    hasLocalVideo: context.canSendVideo,
                                    hasRemoteVideo: context.canRecvVideo,
                                    localVideoSource: context.enabledVideoSource
                                }))
                            ]
                        }
                    }
                },
                holding: {
                    entry: [
                        assign({ isHolding: () => true }),
                        sendParent((context, event) => ({
                            type: 'CALL_STATE_ONHOLD',
                            instanceId: context.instanceId,
                            hasLocalAudio: context.canSendAudio,
                            hasRemoteAudio: context.canRecvAudio,
                            hasLocalVideo: context.canSendVideo,
                            hasRemoteVideo: context.canRecvVideo,
                            localVideoSource: context.enabledVideoSource
                        }))
                    ],
                    exit: assign({ isHolding: () => false }),
                    on: {
                        DO_HOLD: undefined,
                        DO_RESUME: 'tryingResume',
                        DO_SEND_DTMF: undefined,
                        DO_BLIND_XFER: { actions: [forwardTo('executeBlindXfer'), assign({ isLastXferBlind: () => true, isLastXferSuper: () => false })] },
                        DO_XFER: { actions: [forwardTo('executeXfer'), assign({ isLastXferBlind: () => false, isLastXferSuper: () => true })] },
                        TRANSFER: {
                            actions: sendParent((context, event) => ({
                                type: 'HANDLE_REFER',
                                instanceId: context.instanceId,
                                refer_event: event
                            }))
                        },
                        UPDATINGCALL_SUCCEEDED: {
                            actions: 
                                assign({
                                    preferSendAudio: (context, event) => event.preferSendAudio,
                                    preferRecvAudio: (context, event) => event.preferRecvAudio,
                                    preferSendVideo: (context, event) => event.preferSendVideo,
                                    preferRecvVideo: (context, event) => event.preferRecvVideo,
                                    canSendAudio: (context, event) => event.canSendAudio,
                                    canRecvAudio: (context, event) => event.canRecvAudio,
                                    canSendVideo: (context, event) => event.canSendVideo,
                                    enabledVideoSource: (context, event) => event.canSendVideo && context.videoSourceProcessed
                                })
                            }
                    }
                },
                tryingResume: {
                    invoke: {
                        id: 'resumeCall',
                        src: 'resumeCallService',
                        onError: {
                            target: 'holding'
                        }
                    },
                    entry:[
                        assign({ isVideoProcessingComplete: () => false }),
                        assign({ doVideoProcessed: context => context.doVideo, videoSourceProcessed: context => context.videoSource }),
                        assign ({retry491Count: (context, event) => event.type == 'RETRY_491' ? (context.retry491Count + 1):0}),
                        send((context, event) => ({ type: 'DO_HANGUP' }), { delay: 8000, id: 'TRYING_RESUME_TIMER' })
                    ],
                    exit: [cancel('TRYING_RESUME_TIMER'), cancel('RETRY_491_TIMER')],
                    on: {
                        RETRY_491: { target: 'tryingResume', internal: false },
                        // wrong state shall be treated as 491 invite error
                        SIP_ERROR: { actions: raise({ type: 'INVITE_ERROR', result: { code: 491} }), cond: (context, event) => event.error_code === 447},
                        INVITE_ERROR: [
                            // no more than 3 retries in a row
                            { actions: 'doHangupAction', cond: (context, event) => (event.result["code"] !== 491 || context.retry491Count >= 3) },
                            {
                                actions: [
                                    cancel('TRYING_RESUME_TIMER'),
                                    send((context, event) => ({ type: 'RETRY_491' }),
                                    { delay: (context) => {
                                        let max = context.isCallee ? 400 : 200;
                                        let min = context.isCallee ? 210 : 0;
                                        let delay = 10 * Math.floor(Math.random() * (max - min + 1) + min)
                                        Janus.log("Delaying handling of INVITE_ERROR by " + delay + " ms");
                                        return delay;
                                    },
                                    id: 'RETRY_491_TIMER' })
                                ]
                            },
                        ],
                        DO_HOLD: undefined,
                        DO_RESUME: undefined,
                        DO_SEND_DTMF: undefined,
                        RESUMING: {
                            target: undefined,
                            actions:
                                assign({
                                    preferSendAudio: (context, event) => event.preferSendAudio,
                                    preferRecvAudio: (context, event) => event.preferRecvAudio,
                                    preferSendVideo: (context, event) => event.preferSendVideo,
                                    preferRecvVideo: (context, event) => event.preferRecvVideo,
                                    canSendAudio: (context, event) => event.canSendAudio,
                                    canRecvAudio: (context, event) => event.canRecvAudio,
                                    canSendVideo: (context, event) => event.canSendVideo,
                                    canRecvVideo: (context, event) => event.canRecvVideo,
                                })
                        },
                        REMOTE_SDP_HANDLING_SUCCEEDED: {
                            target: 'notHold',
                            actions: [
                                sendParent((context, event) => ({
                                    type: 'CALL_STATE_CONNECTED',
                                    instanceId: context.instanceId,
                                    isInitial: false,
                                    hasLocalAudio: event.canSendAudio,
                                    hasRemoteAudio: event.canRecvAudio,
                                    hasLocalVideo: event.canSendVideo,
                                    hasRemoteVideo: event.canRecvVideo,
                                    localVideoSource: event.enabledVideoSource,
                                    isIceRestartRequired: context => false,
                                })),
                                assign({
                                    isVideoProcessingComplete: () => true,
                                    canSendAudio: (context, event) => event.canSendAudio,
                                    canRecvAudio: (context, event) => event.canRecvAudio,
                                    canSendVideo: (context, event) => event.canSendVideo,
                                    canRecvVideo: (context, event) => event.canRecvVideo,
                                    localVideoSource: (context, event) => event.enabledVideoSource
                                })
                            ]
                        },
                        DO_RESUME_FAILED: [
                            sendParent((context, event) => ({ 
                                type: 'CALL_STATE_ERROR', 
                                instanceId: context.instanceId, 
                                errorCode: GetErrorMessage(event).toLowerCase().includes('permission') ? 
                                    CallStatusErrorCode.ERROR_RESUME_PERMISSION : CallStatusErrorCode.ERROR_RESUME,
                                errorMessage: 'Mid-call signalling failure:' + GetErrorMessage(event) })),                            
                            'holding'
                        ],
                    }
                },
            }
        },
    } // states
},
    {
        guards: {
            xferOk: (context, event) => {
                Janus.log("Checking xferOK condition, isLastXferBlind=", context.isLastXferBlind, "isLastXferSuper=", context.isLastXferSuper);
                if ((context.isLastXferBlind || context.isLastXferSuper) && event.result["notify"] === "refer" && event.result["content"] !== undefined) {
                    let splitContent = event.result["content"].split(" ");
                    if (splitContent.length > 1 && splitContent[1] >= +180 && splitContent[1] < +300) {
                        Janus.log("xferOK condition is true");
                        return true;
                    }
                }
                Janus.log("xferOK condition is false");
                return false;
            },
            blindXferFailed: (context, event) => {
                Janus.log("Checking blindXferFailed condition...");
                if (context.isLastXferBlind && event.result["notify"] === "refer" && event.result["content"] !== undefined) {
                    let splitContent = event.result["content"].split(" ");
                    if (splitContent.length > 1 && splitContent[1] >= +300) {
                        Janus.log("blindXferFailed condition is true");
                        return true;
                    }
                }
                Janus.log("blindXferFailed condition is false");
                return false;
            },
            xferFailed: (context, event) => {
                Janus.log("Checking xferFailed condition...");
                if (context.isLastXferSuper && event.result["notify"] === "refer" && event.result["content"] !== undefined) {
                    let splitContent = event.result["content"].split(" ");
                    if (splitContent.length > 1 && splitContent[1] >= +300) {
                        Janus.log("xferFailed condition is true");
                        return true;
                    }
                }
                Janus.log("xferFailed condition is false");
                return false;
            },
            xferProgress: (context, event) => {
                Janus.log("Checking xferProgress condition...");
                if ((context.isLastXferBlind || context.isLastXferSuper) && event.result["notify"] === "refer" && event.result["content"] !== undefined) {
                    Janus.log("xferProgress condition is true");
                    return true;
                }
                Janus.log("xferProgress condition is false");
                return false;
            },
        },
        actions: {
            doHangupAction: (context, event) => {
                if (event.message !== undefined) {
                    Janus.debug("hanging up with message:", GetErrorMessage(event));
                }
                Janus.log("doHangupAction with event:", event);
                context.pluginHandle.hangup(true);
            },
            doSendDtmfAction: (context, event) => {
                context.pluginHandle.dtmf({ dtmf: { tones: event.dtmf } });
            },
            logEvent: (context, event) => {
                Janus.log("!!! Context: ", context);
                Janus.log("!!! Event: ", event);
            },
            declineTransferAction: (context, event) => {
                Janus.log("Declining transfer request");
                let referId = event.result["refer_id"];
                let body = { request: "decline", refer_id: referId, code: 403 };
                context.pluginHandle.send({ "message": body });
            },
            messageAckIncomingCallAction: (context, event) => {
                Janus.log("Sending message to ack incoming call");
                let body = { request: "message", content: "Your call is important to us!" };
                context.pluginHandle.send({ "message": body });
            },
            logDoMakeCallAction: (context, event) => {
                Janus.log("DO_MAKE_CALL Context: ", context);
                Janus.log("DO_MAKE_CALL Event: ", event);
            },
            logJanusSessionEnded: (context, event) => {
                Janus.log("JANUS_SESSION_ENDED Context: ", context);
                Janus.log("JANUS_SESSION_ENDED Event: ", event);
            },
            logContext:  (context, event) => {
                Janus.log("Log Context: ", context);
                Janus.log("Log Event: ", event);
            },
        },
        services: {
            restartIceService: (context, event) => (callback, onReceive) => {
                try {
                    Janus.log("Restart ICE service with event:", event);
                    let sendAudio = context.canSendAudio;
                    let recvAudio = context.canRecvAudio;
                    let sendVideo = context.doVideo;
                    let recvVideo = context.canRecvVideo;
                    let replaceVideo = HasVideoTracks(context);


                    Janus.log("Restarting ICE with: ", "sendAudio=", sendAudio, "recvAudio=", recvAudio);
                    Janus.log("Restarting ICE with: ", "sendVideo=", sendVideo, "recvVideo=", recvVideo);
                    context.pluginHandle.createOffer({
                        media: {
                            useUserMedia: (isElectron && context.videoSource === 'screen'),
                            replaceAudio: true,
                            audioSend: sendAudio,
                            audioRecv: recvAudio,
                            videoSend: sendVideo,
                            videoRecv: recvVideo,
                            audio: {
                                autoGainControl: defaultMediaConstraints?.autoGainControl ?? false,
                                echoCancellation: (context.videoSource && context.videoSource === 'screen') ? false : (defaultMediaConstraints?.echoCancellation ?? false),
                                restrictOwnAudio: (context.videoSource && context.videoSource === 'screen') ? false : undefined,
                                noiseSuppression: defaultMediaConstraints?.noiseSuppression ?? false,
                                channelCount: defaultMediaConstraints?.channelCount,
                                sampleRate: defaultMediaConstraints?.sampleRate,
                                sampleSize: defaultMediaConstraints?.sampleSize,
                                mandatory: (isElectron && context.videoSource === 'screen') ? {chromeMediaSource: 'desktop'} : undefined

                            },
                            replaceVideo: replaceVideo,
                            video: (isElectron && context.videoSource === 'screen') ? {mandatory: {chromeMediaSource: 'desktop'} } : context.videoSource, // can be 'screen', 'window' or undefined
                            captureDesktopAudio: context.videoSource === 'screen',
                            screenshareFrameRate: context.videoSource === 'screen' ? context.srFrameRate : undefined,
                            screenshareWidth: context.videoSource === 'screen' ? context.srScreenWidth : undefined,
                            screenshareHeight: context.videoSource === 'screen' ? context.srScreenHeight : undefined
        
                        },
                        iceRestart: true,
                        customizeSdp: function (jsep) {
                            Janus.log("customizeSdp before:", jsep)
                            FixInactiveSdpAttr(jsep, sendAudio, recvAudio, sendVideo, recvVideo);
                            if (context.videoSource === 'screen')
                            {
                                EnableStereoAudio(jsep);
                            }
                            Janus.log("customizeSdp after:", jsep)
                        },
                        success: (jsep) => {
                            try {
                                Janus.debug("Got SDP!");
                                Janus.debug(jsep);
                                let body = { request: "update" };
                                context.pluginHandle.send({ "message": body, "jsep": jsep });
                            } catch (error) {
                                Janus.log("Error restarting ICE, error: ", error.message);
                                callback({ type: 'ICE_RESTART_FAILED', message: error.message });
                            }
                        },
                        error: (message) => {
                            Janus.log("Error restarting ICE, error: ", message);
                            callback({ type: 'ICE_RESTART_FAILED', message: message });
                        },
                    });
                } catch (error) {
                    Janus.log("Error restarting ICE, error: ", error.message);
                    callback({ type: 'ICE_RESTART_FAILED', message: error.message });
                }
            },
            updateVideoService: (context, event) => (callback, onReceive) => {
                try {
                    Janus.log("Update video service with event:", event);
                    let sendAudio = context.canSendAudio;
                    let recvAudio = context.canRecvAudio;
                    let sendVideo = context.doVideo;
                    let recvVideo = context.canRecvVideo || context.doVideo;
                    let replaceVideo = HasVideoTracks(context) && (sendVideo || recvVideo);
                    let removeVideo = HasVideoTracks(context) && !sendVideo && !recvVideo;
                    let addVideo = !replaceVideo && context.doVideo && context.retry491Count == 0;



                    Janus.log("Updating video with: ", "sendAudio=", sendAudio, "recvAudio=", recvAudio);
                    Janus.log("Updating video with: ", "sendVideo=", sendVideo, "recvVideo=", recvVideo);
                    if (context.isIceRestartRequired) {
                        Janus.log("Updating video with ICE restart");
                    }
                    context.pluginHandle.createOffer({
                        media: {
                            useUserMedia: (isElectron && context.videoSource === 'screen'),
                            replaceAudio: true,
                            audioSend: sendAudio,
                            audioRecv: recvAudio,
                            videoSend: sendVideo,
                            videoRecv: recvVideo,
                            removeVideo: removeVideo,
                            audio: {
                                autoGainControl: defaultMediaConstraints?.autoGainControl ?? false,
                                echoCancellation: (context.videoSource && context.videoSource === 'screen') ? false : (defaultMediaConstraints?.echoCancellation ?? false),
                                restrictOwnAudio: (context.videoSource && context.videoSource === 'screen') ? false : undefined,
                                noiseSuppression: defaultMediaConstraints?.noiseSuppression ?? false,
                                channelCount: defaultMediaConstraints?.channelCount,
                                sampleRate: defaultMediaConstraints?.sampleRate,
                                sampleSize: defaultMediaConstraints?.sampleSize,
                                mandatory: (isElectron && context.videoSource === 'screen') ? {chromeMediaSource: 'desktop'} : undefined
                            },
                            addVideo: addVideo,
                            replaceVideo: replaceVideo,
                            video: (isElectron && context.videoSource === 'screen') ? {mandatory: {chromeMediaSource: 'desktop'} } : context.videoSource, // can be 'screen', 'window' or undefined
                            captureDesktopAudio: context.videoSource === 'screen',
                            screenshareFrameRate: context.videoSource === 'screen' ? context.srFrameRate : undefined,
                            screenshareWidth: context.videoSource === 'screen' ? context.srScreenWidth : undefined,
                            screenshareHeight: context.videoSource === 'screen' ? context.srScreenHeight : undefined
                        },
                        iceRestart: context.isIceRestartRequired,
                        customizeSdp: function (jsep) {
                            Janus.log("customizeSdp before:", jsep)
                            FixInactiveSdpAttr(jsep, sendAudio, recvAudio, sendVideo, recvVideo);
                            if (context.videoSource === 'screen')
                            {
                                EnableStereoAudio(jsep);
                            }
                            Janus.log("customizeSdp after:", jsep)
                        },
                        success: (jsep) => {
                            try {
                                Janus.debug("Got SDP!");
                                Janus.debug(jsep);
                                let body = { request: "update" };
                                context.pluginHandle.send({ "message": body, "jsep": jsep });
                                callback({
                                    type: 'UPDATING_MEDIA',
                                    preferSendAudio: sendAudio,
                                    preferRecvAudio: recvAudio,
                                    preferSendVideo: sendVideo,
                                    preferRecvVideo: recvVideo,
                                });
                            } catch (error) {
                                Janus.log("Error updating call media, error: ", error.message);
                                callback({ type: 'UPDATE_MEDIA_FAILED', message: error.message });
                            }
                        },
                        error: (message) => {
                            Janus.log("Error updating call media, error: ", message);
                            callback({ type: 'UPDATE_MEDIA_FAILED', message: message });
                        },
                    });
                } catch (error) {
                    callback({ type: 'UPDATE_MEDIA_FAILED', message: error.message });
                }
            },
            holdCallService: (context, event) => (callback, onReceive) => {
                try {
                    Janus.log("Hold call service with event:", event);
                    let sendAudio = false;
                    let recvAudio = false;
                    let sendVideo = false;
                    let recvVideo = false;
                    let replaceVideo = HasVideoTracks(context) && (sendVideo || recvVideo);
                    let removeVideo = HasVideoTracks(context) && !sendVideo && !recvVideo;

                    Janus.log("Holding with: ", "sendAudio=", sendAudio, "recvAudio=", recvAudio);
                    Janus.log("Holding with: ", "sendVideo=", sendVideo, "recvVideo=", recvVideo);
                    if (context.isIceRestartRequired) {
                        Janus.log("Holding with ICE restart");
                    }
                    context.pluginHandle.createOffer({
                        media: {
                            replaceAudio: true,
                            audioSend: sendAudio,
                            audioRecv: recvAudio,
                            videoSend: sendVideo,
                            videoRecv: recvVideo,
                            replaceVideo: replaceVideo,
                            removeVideo: removeVideo,
                        },
                        iceRestart: context.isIceRestartRequired,
                        customizeSdp: function (jsep) {
                            FixInactiveSdpAttr(jsep, sendAudio, recvAudio, sendVideo, recvVideo);                            
                        },
                        success: (jsep) => {
                            try {
                                Janus.debug("Got SDP!");
                                Janus.debug(jsep);
                                let body = { request: "update" };
                                context.pluginHandle.send({ "message": body, "jsep": jsep });
                                callback({
                                    type: 'HOLDING',
                                    preferSendAudio: sendAudio,
                                    preferRecvAudio: recvAudio,
                                    preferSendVideo: sendVideo,
                                    preferRecvVideo: recvVideo,
                                    canSendAudio: sendAudio,
                                    canRecvAudio: recvAudio,
                                    canSendVideo: sendVideo,
                                    canRecvVideo: recvVideo
                                });
                            } catch (error) {
                                Janus.log("Error holding call, error: ", error.message);
                                callback({ type: 'DO_HOLD_FAILED', message: error.message });
                            }
                        },
                        error: (message) => {
                            Janus.log("Error holding call, error: ", message);
                            callback({ type: 'DO_HOLD_FAILED', message: message });
                        },
                    });
                } catch (error) {
                    callback({ type: 'DO_HOLD_FAILED', message: error.message });
                }
            },
            resumeCallService: (context, event) => (callback, onReceive) => {
                try {
                    Janus.log("Resume call service with event:", event, ", context: ", context);
                    let sendAudio = true;
                    let recvAudio = true;
                    let sendVideo = context.doVideo;
                    let recvVideo = context.preferRecvVideo || context.canRecvVideo || context.doVideo;
                    let replaceVideo = HasVideoTracks(context) && (sendVideo || recvVideo);
                    let removeVideo = HasVideoTracks(context) && !sendVideo && !recvVideo;



                    Janus.log("Resuming with: ", "sendAudio=", sendAudio, "recvAudio=", recvAudio);
                    Janus.log("Resuming with: ", "sendVideo=", sendVideo, "recvVideo=", recvVideo);
                    if (context.isIceRestartRequired) {
                        Janus.log("Resuming with ICE restart");
                    }
                    context.pluginHandle.createOffer({
                        media: {
                            replaceAudio: true,
                            audioSend: sendAudio,
                            audioRecv: recvAudio,
                            audio: {
                                autoGainControl: defaultMediaConstraints?.autoGainControl ?? false,
                                echoCancellation: (context.videoSource && context.videoSource === 'screen') ? false : (defaultMediaConstraints?.echoCancellation ?? false),
                                restrictOwnAudio: (context.videoSource && context.videoSource === 'screen') ? false : undefined,
                                noiseSuppression: defaultMediaConstraints?.noiseSuppression ?? false,
                                channelCount: defaultMediaConstraints?.channelCount,
                                sampleRate: defaultMediaConstraints?.sampleRate,
                                sampleSize: defaultMediaConstraints?.sampleSize,
                            },
                            replaceVideo: replaceVideo,
                            removeVideo: removeVideo,
                            videoSend: sendVideo,
                            videoRecv: recvVideo,
                            video: sendVideo ? context.enabledVideoSource : undefined,
                            captureDesktopAudio: sendVideo && context.videoSource === 'screen',
                            screenshareFrameRate: sendVideo && context.videoSource === 'screen' ? context.srFrameRate : undefined,
                            screenshareWidth: context.videoSource === 'screen' ? context.srScreenWidth : undefined,
                            screenshareHeight: context.videoSource === 'screen' ? context.srScreenHeight : undefined
                        },
                        iceRestart: context.isIceRestartRequired,
                        customizeSdp: function (jsep) {
                            FixInactiveSdpAttr(jsep, sendAudio, recvAudio, sendVideo, recvVideo);
                            if (sendVideo && context.videoSource === 'screen') {
                                EnableStereoAudio(jsep);
                            }
                        },
                        success: (jsep) => {
                            try {
                                Janus.debug("Got SDP!");
                                Janus.debug(jsep);
                                let body = { request: "update" };
                                context.pluginHandle.send({ "message": body, "jsep": jsep });
                                callback({
                                    type: 'RESUMING',
                                    preferSendAudio: sendAudio,
                                    preferRecvAudio: recvAudio,
                                    preferSendVideo: sendVideo,
                                    preferRecvVideo: recvVideo,
                                    canSendAudio: sendAudio,
                                    canRecvAudio: recvAudio,
                                    canSendVideo: sendVideo,
                                    canRecvVideo: recvVideo
                                });
                            } catch (error) {
                                Janus.log("Error resuming call, error: ", error.message);
                                callback({ type: 'DO_RESUME_FAILED', message: error.message });
                            }
                        },
                        error: (message) => {
                            Janus.log("Error resuming call, error: ", message );
                            callback({ type: 'DO_RESUME_FAILED', message: message });
                        },
                    });
                } catch (error) {
                    callback({ type: 'DO_RESUME_FAILED', message: error.message });
                }
            },

            // get SDP offer
            makeCallService: (context, event) => (callback, onReceive) => {
                try {
                    Janus.log("Creating offer with event:", event, ", video has been requested: ", context.doVideo);
                    context.preferSendAudio = context.instanceId !== 4 || context.optionRecordAudio === "true";
                    //context.preferRecvAudio = true;
                    context.preferRecvAudio = !(context.doVideo && context.videoSource === 'screen');
                    context.preferSendVideo = context.doVideo;
                    //context.preferRecvVideo = context.doVideo;
                    context.preferRecvVideo = context.doVideo && context.videoSource !== 'screen';
                    Janus.log("Offering with: ", "preferSendAudio=", context.preferSendAudio, "preferRecvAudio=", context.preferRecvAudio);
                    Janus.log("Offering with: ", "preferSendVideo=", context.preferSendVideo, "preferRecvVideo=", context.preferRecvVideo);

                    let audio = (context.preferSendAudio || context.preferRecvAudio) ? {
                        autoGainControl: defaultMediaConstraints?.autoGainControl ?? false,
                        echoCancellation: (context.videoSource && context.videoSource === 'screen') ? false : (defaultMediaConstraints?.echoCancellation ?? false),
                        restrictOwnAudio: (context.videoSource && context.videoSource === 'screen') ? false : undefined,
                        noiseSuppression: defaultMediaConstraints?.noiseSuppression ?? false,
                        channelCount: defaultMediaConstraints?.channelCount,
                        sampleRate: defaultMediaConstraints?.sampleRate,
                        sampleSize: defaultMediaConstraints?.sampleSize,
                    } : false;
                    if (audio && (isElectron && context.videoSource === 'screen')) {
                        // if electron need to have chromeMediaSource in mandatory. therefore everything has to be
                        audio = {
                            mandatory: {
                                ...audio,
                                chromeMediaSource: 'desktop'
                            }
                        }
                    }
                    let video = context.doVideo
                        ? (isElectron && context.videoSource === 'screen') 
                            ? { capture: context.videoSource, mandatory: {chromeMediaSource: 'desktop'} } 
                            : context.videoSource
                        : undefined;
                    context.pluginHandle.createOffer({
                        media: {
                            useUserMedia: (isElectron && context.videoSource === 'screen'),
                            audioSend: context.preferSendAudio,
                            audioRecv: context.preferRecvAudio,
                            videoSend: context.preferSendVideo,
                            videoRecv: context.preferRecvVideo,
                            audio: audio,
                            video: video,
                            captureDesktopAudio: context.doVideo && context.videoSource === 'screen',
                            screenshareFrameRate: context.doVideo && context.videoSource === 'screen' ? context.srFrameRate : undefined,
                            screenshareWidth: context.videoSource === 'screen' ? context.srScreenWidth : undefined,
                            screenshareHeight: context.videoSource === 'screen' ? context.srScreenHeight : undefined
                        },
                        customizeSdp: function (jsep) {
                            if (context.doVideo && context.videoSource === 'screen') {
                                EnableStereoAudio(jsep);
                            }
                        },
                        success: (jsep) => {
                            try {
                                Janus.debug("Got SDP!");
                                Janus.debug(jsep);
                                let body = { request: "call", uri: event.target };
                                if (context.instanceId !== 0) {
                                    body["headers"] = { "Janus-Helper-Id": ("C" + context.instanceId) };
                                }
                                if (context.optionCaptureOnDemand && context.optionCaptureOnDemand !== 0) {
                                    let tagValue = context.optionCaptureOnDemand.toString() + ";tag=" 
                                        + (context.optionCaptureOnDemandTag ? ("\"" + context.optionCaptureOnDemandTag  + "\"") : "none")
                                        + ";record-audio="
                                        + (context.optionRecordAudio ? "true" : "false");
                                    if (!body.headers) {
                                        body["headers"] = {};
                                    }
                                    body["headers"]["IPFX-ODSR-Option-Tag"] = tagValue;
                                }
                                if (event.referId) {
                                    // In case we're originating this call because of a call
                                    // transfer, we need to provide the internal reference ID
                                    body["refer_id"] = event.referId;
                                }
                                if (event.callId) {
                                    body["call_id"] = event.callId;
                                }
                                body["autoaccept_reinvites"] = false;
                                context.pluginHandle.send({ "message": body, "jsep": jsep });
                                callback({
                                    type: 'DO_MAKE_CALL_SUCCEEDED',
                                    jsep: jsep,
                                    preferSendAudio: context.preferSendAudio,
                                    preferRecvAudio: context.preferRecvAudio,
                                    preferSendVideo: context.preferSendVideo,
                                    preferRecvVideo: context.preferRecvVideo
                                });
                            } catch (error) {
                                callback({ type: 'DO_MAKE_CALL_FAILED', message: error.message });
                            }
                        },
                        error: (message) => {
                            callback({ type: 'DO_MAKE_CALL_FAILED', message: message })
                        },
                    });
                } catch (error) {
                    callback({ type: 'DO_MAKE_CALL_FAILED', message: error.message });
                }
            },
            handleRemoteMediaService: (context, event) => (callback, onReceive) => {
                onReceive(event => {
                    let e = event.event;
                    let context = event.context;
                    let local_context = event.context;
                    local_context.preferRecvVideo = (e.jsep && e.jsep.sdp && e.jsep.sdp.includes("m=video")) || context.doVideo;
                    local_context.canRecvVideo = local_context.canRecvVideo || local_context.preferRecvVideo;


                    if (e.type === 'UPDATINGCALL') {
                        // create answer SDP
                        try {
                            let offerless = !e.jsep || !e.jsep.sdp;
                            Janus.log("Got re-INVITE ", (offerless ? "without" : "with"), " SDP offer, context:", local_context);
                            local_context.preferSendVideo = local_context.doVideo;
                            AdjustMediaMode(local_context, offerless ? null : e.jsep.sdp, true);
                            Janus.log("preferSendAudio=", local_context.preferSendAudio, "preferRecvAudio=", local_context.preferRecvAudio);
                            Janus.log("preferSendVideo=", local_context.preferSendVideo, "preferRecvVideo=", local_context.preferRecvVideo);
                            if (offerless) {
                                Janus.log("Offering with: ", "canSendAudio=", local_context.canSendAudio, "canRecvAudio=", local_context.canRecvAudio);
                                Janus.log("Offering with: ", "canSendVideo=", local_context.canSendVideo, "canRecvVideo=", local_context.canRecvVideo);
                            } else {
                                Janus.log("Answering with: ", "canSendAudio=", local_context.canSendAudio, "canRecvAudio=", local_context.canRecvAudio);
                                Janus.log("Answering with: ", "canSendVideo=", local_context.canSendVideo, "canRecvVideo=", local_context.canRecvVideo);
                            }
                            FixInactiveSdpAttr(e.jsep, local_context.canSendAudio, local_context.canRecvAudio, local_context.canSendVideo, local_context.canRecvVideo);
                            let sipcallAction = (offerless ? local_context.pluginHandle.createOffer : local_context.pluginHandle.createAnswer);
                            sipcallAction({
                                jsep: e.jsep,
                                media: {
                                    useUserMedia: (isElectron && context.videoSource === 'screen'),
                                    audio: {
                                        autoGainControl: defaultMediaConstraints?.autoGainControl ?? false,
                                        echoCancellation: (context.videoSource && context.videoSource === 'screen') ? false : (defaultMediaConstraints?.echoCancellation ?? false),
                                        restrictOwnAudio: (context.videoSource && context.videoSource === 'screen') ? false : undefined,
                                        noiseSuppression: defaultMediaConstraints?.noiseSuppression ?? false,
                                        channelCount: defaultMediaConstraints?.channelCount,
                                        sampleRate: defaultMediaConstraints?.sampleRate,
                                        sampleSize: defaultMediaConstraints?.sampleSize,
                                        mandatory: (isElectron && context.videoSource === 'screen') ? {chromeMediaSource: 'desktop'} : undefined
                                    },
                                    replaceAudio: offerless ? undefined : true,
                                    audioSend: local_context.canSendAudio,
                                    audioRecv: local_context.canRecvAudio,
                                    videoSend: local_context.canSendVideo,
                                    videoRecv: local_context.canRecvVideo,
                                    replaceVideo: HasVideoTracks(context) && (local_context.canSendVideo || local_context.canRecvVideo),
                                    removeVideo: HasVideoTracks(context) && !local_context.canSendVideo && !local_context.canRecvVideo,
                                    video: local_context.canSendVideo ? ((isElectron && context.videoSource === 'screen') ? {mandatory: {chromeMediaSource: 'desktop'} } : local_context.videoSource) : undefined,
                                    captureDesktopAudio: local_context.canSendVideo && local_context.videoSource === 'screen',
                                    screenshareFrameRate: local_context.canSendVideo && context.videoSource === 'screen' ? context.srFrameRate : undefined,
                                    screenshareWidth: context.videoSource === 'screen' ? context.srScreenWidth : undefined,
                                    screenshareHeight: context.videoSource === 'screen' ? context.srScreenHeight : undefined
                                },
                                customizeSdp: function (jsep) {
                                    FixInactiveSdpAttr(jsep, local_context.canSendAudio, local_context.canRecvAudio, local_context.canSendVideo, local_context.canRecvVideo);
                                    if (local_context.canSendVideo && local_context.videoSource === 'screen') {
                                        EnableStereoAudio(jsep);
                                    }
                                },
                                success: function (jsep) {
                                    try {
                                        let body = { request: "update" };
                                        context.pluginHandle.send({ "message": body, "jsep": jsep });
                                        if (!offerless) {
                                            callback({
                                                type: 'UPDATINGCALL_SUCCEEDED',
                                                preferSendAudio: local_context.preferSendAudio,
                                                preferRecvAudio: local_context.preferRecvAudio,
                                                preferSendVideo: local_context.preferSendVideo,
                                                preferRecvVideo: local_context.preferRecvVideo,
                                                canSendAudio: local_context.canSendAudio,
                                                canRecvAudio: local_context.canRecvAudio,
                                                canSendVideo: local_context.canSendVideo,
                                                canRecvVideo: local_context.canRecvVideo,
                                                enabledVideoSource: local_context.canSendVideo ? local_context.videoSourceProcessed : undefined
                                            });
                                        }
                                    } catch (error) {
                                        Janus.error("Failed to update with answer, error", error);
                                        callback({ type: 'UPDATINGCALL_FAILED', message: error });
                                    }
                                },
                                error: function (message) {
                                    Janus.error("WebRTC error:", message);
                                    callback({ type: 'UPDATINGCALL_FAILED', message: message });
                                }
                            });
                        } catch (error) {
                            callback({ type: 'UPDATINGCALL_FAILED', message: error.message });
                        }
                    } else if (e.type === 'ACCEPTED') {
                        try {
                            if (!e.jsep || !e.jsep.sdp) {
                                Janus.log("Got re-INVITE answer without SDP answer");
                                callback({ type: 'REMOTE_SDP_HANDLING_FAILED', message: 'No SDP found' });
                                return;
                            }
                            Janus.log("Got hold/resume answer");
                            Janus.log("preferSendAudio=", local_context.preferSendAudio, "preferRecvAudio=", local_context.preferRecvAudio);
                            Janus.log("preferSendVideo=", local_context.preferSendVideo, "preferRecvVideo=", local_context.preferRecvVideo);
                            AdjustMediaMode(local_context, e.jsep.sdp)
                            Janus.log("Accepting with: ", "canSendAudio=", local_context.canSendAudio, "canRecvAudio=", local_context.canRecvAudio);
                            Janus.log("Accepting with: ", "canSendVideo=", local_context.canSendVideo, "canRecvVideo=", local_context.canRecvVideo);
                            FixInactiveSdpAttr(e.jsep, local_context.canSendAudio, local_context.canRecvAudio, local_context.canSendVideo, local_context.canRecvVideo);
                            context.pluginHandle.handleRemoteJsep({
                                jsep: e.jsep,
                                success: () => {
                                    callback({
                                        type: 'REMOTE_SDP_HANDLING_SUCCEEDED',
                                        canSendAudio: local_context.canSendAudio,
                                        canRecvAudio: local_context.canRecvAudio,
                                        canSendVideo: local_context.canSendVideo,
                                        canRecvVideo: local_context.canRecvVideo,
                                        enabledVideoSource: local_context.canSendVideo ? local_context.videoSourceProcessed : undefined
                                    });
                                },
                                error: (message) => {
                                    callback({ type: 'REMOTE_SDP_HANDLING_FAILED', message: message });
                                },
                            });
                        } catch (error) {
                            callback({ type: 'REMOTE_SDP_HANDLING_FAILED', message: error.message });
                        }
                    }
                });
                // handle remote SDP
                if (event.jsep && event.jsep.sdp) {
                    try {
                        let local_context = {
                            preferSendAudio: context.preferSendAudio,
                            preferRecvAudio: context.preferRecvAudio,
                            preferSendVideo: context.doVideo,
                            preferRecvVideo: context.preferRecvVideo,
                            canSendAudio: context.canSendAudio,
                            canRecvAudio: context.canRecvAudio,
                            canSendVideo: context.canSendVideo,
                            canRecvVideo: context.canRecvVideo,
                            videoSourceProcessed: context.videoSourceProcessed,
                        };

                        Janus.log("preferSendAudio=", local_context.preferSendAudio, "preferRecvAudio=", local_context.preferRecvAudio);
                        Janus.log("preferSendVideo=", local_context.preferSendVideo, "preferRecvVideo=", local_context.preferRecvVideo);
                        AdjustMediaMode(local_context, event.jsep.sdp)
                        Janus.log("Accepting with: ", "canSendAudio=", local_context.canSendAudio, "canRecvAudio=", local_context.canRecvAudio);
                        Janus.log("Accepting with: ", "canSendVideo=", local_context.canSendVideo, "canRecvVideo=", local_context.canRecvVideo);
                        FixInactiveSdpAttr(event.jsep, local_context.canSendAudio, local_context.canRecvAudio, local_context.canSendVideo, local_context.canRecvVideo);
                        context.pluginHandle.handleRemoteJsep({
                            jsep: event.jsep,
                            success: () => {
                                callback({
                                    type: 'REMOTE_SDP_HANDLING_SUCCEEDED',
                                    canSendAudio: local_context.canSendAudio,
                                    canRecvAudio: local_context.canRecvAudio,
                                    canSendVideo: local_context.canSendVideo,
                                    canRecvVideo: local_context.canRecvVideo,
                                    enabledVideoSource: local_context.canSendVideo ? local_context.videoSourceProcessed : undefined
                                 });
                            },
                            error: (message) => {
                                callback({ type: 'REMOTE_SDP_HANDLING_FAILED', message: message });
                            },
                        });
                    } catch (error) {
                        callback({ type: 'REMOTE_SDP_HANDLING_FAILED', message: error.message });
                    }
                }
            },
            handleIncomingCallService: (context, event) => (callback, onReceive) => {
                onReceive(e => {
                    if (e.type === "DO_ACCEPT") {
                        // accepting call
                        try {
                            let jsep = context.incomingCallJsep;
                            let offerless = !jsep || !jsep.sdp;
                            context.preferSendAudio = true;
                            context.preferRecvAudio = true;
                            context.preferSendVideo = context.doVideo;
                            context.preferRecvVideo = (jsep && jsep.sdp && jsep.sdp.includes("m=video")) || context.doVideo;
                            context.canSendAudio = context.preferSendAudio;
                            context.canRecvAudio = context.preferRecvAudio;
                            context.canSendVideo = context.preferSendVideo;
                            context.canRecvVideo = context.preferRecvVideo;
                            AdjustMediaMode(context, offerless ? null : jsep.sdp);
                            if (offerless) {
                                Janus.log("Offering with: ", "preferSendAudio=", context.preferSendAudio, "preferRecvAudio=", context.preferRecvAudio);
                                Janus.log("Offering with: ", "preferSendVideo=", context.preferSendVideo, "preferRecvVideo=", context.preferRecvVideo);
                            } else {
                                Janus.log("Answering with: ", "canSendAudio=", context.canSendAudio, "preferRecvAudio=", context.preferRecvAudio);
                                Janus.log("Answering with: ", "canSendVideo=", context.canSendVideo, "canRecvVideo=", context.canRecvVideo);
                                if (context.doVideo && !context.canSendVideo) {
                                    Janus.log("Scheduling reINVITE when connected to enable send video");
                                    callback('DO_POST_ENABLE_VIDEO');
                                }
                            }
                            let sipcallAction = offerless ? context.pluginHandle.createOffer : context.pluginHandle.createAnswer;
                            sipcallAction({
                                jsep: jsep,
                                media: {
                                    useUserMedia: (isElectron && context.videoSource === 'screen'),
                                    audioSend: context.canSendAudio,
                                    audioRecv: context.canRecvAudio,
                                    videoSend: context.canSendVideo,
                                    videoRecv: context.canRecvVideo,
                                    audio: {
                                        autoGainControl: defaultMediaConstraints?.autoGainControl ?? false,
                                        echoCancellation: (context.videoSource && context.videoSource === 'screen') ? false : (defaultMediaConstraints?.echoCancellation ?? false),
                                        restrictOwnAudio: (context.videoSource && context.videoSource === 'screen') ? false : undefined,
                                        noiseSuppression: defaultMediaConstraints?.noiseSuppression ?? false,
                                        channelCount: defaultMediaConstraints?.channelCount,
                                        sampleRate: defaultMediaConstraints?.sampleRate,
                                        sampleSize: defaultMediaConstraints?.sampleSize,
                                        mandatory: (isElectron && context.videoSource === 'screen') ? {chromeMediaSource: 'desktop'} : undefined
                                    },
                                    video: context.canSendVideo ? ((isElectron && context.videoSource === 'screen') ? {mandatory: {chromeMediaSource: 'desktop'} } : context.videoSourceProcessed) : undefined,
                                    captureDesktopAudio: context.canSendVideo && context.videoSource === 'screen',
                                    screenshareFrameRate: context.canSendVideo && context.videoSource === 'screen' ? context.srFrameRate : undefined,
                                    screenshareWidth: context.videoSource === 'screen' ? context.srScreenWidth : undefined,
                                    screenshareHeight: context.videoSource === 'screen' ? context.srScreenHeight : undefined
                                },
                                customizeSdp: function (jsep) {
                                    if (context.canSendVideo && context.videoSource === 'screen') {
                                        EnableStereoAudio(jsep);
                                    }
                                },                                
                                success: function (jsep) {
                                    try {
                                        Janus.debug(jsep);
                                        let body = {
                                            request: "accept",
                                            autoaccept_reinvites: false,
                                        };
                                        if (context.instanceId !== 0) {
                                            body["headers"] = {
                                                "Janus-Helper-Id": ("C" + context.instanceId)
                                            };
                                        }
                                        context.pluginHandle.send({ "message": body, "jsep": jsep });
                                        callback('DO_ACCEPT_SUCCEEDED');
                                    } catch (error) {
                                        callback({ type: 'DO_ACCEPT_FAILED', message: error.message });
                                    }
                                },
                                error: function (message) {
                                    // accept failed
                                    try {
                                        Janus.error("WebRTC error:", message);
                                        let body = { "request": "decline", "code": 480 };
                                        context.pluginHandle.send({ "message": body });
                                        callback({ type: 'DO_ACCEPT_FAILED', message: message });
                                    } catch (err) {
                                        callback({ type: 'DO_ACCEPT_FAILED', message: message + "," + err.message });
                                    }
                                }
                            });

                        } catch (error) {
                            callback({ type: 'DO_ACCEPT_FAILED', message: error.message });
                        }
                    }
                    else if (e.type === "DO_DECLINE") {
                        // declining call
                        try {
                            let body = { "request": "decline" };
                            if (e.code !== undefined && !isNaN(e.code)) {
                                body["code"] = e.code;
                            }
                            context.pluginHandle.send({ "message": body });
                            callback('DO_DECLINE_SUCCEEDED');
                        } catch (error) {
                            callback({ type: 'DO_DECLINE_FAILED', message: error.message });
                        }
                    }
                });
            },
            executeXferService: (context, event) => (callback, onReceive) => {
                onReceive(e => {
                    if (e.type === 'DO_BLIND_XFER') {
                        try {
                            let body = { "request": "transfer", "uri": e.refer_to };
                            context.pluginHandle.send({ "message": body });
                            context.isLastXferBlind = true;
                            context.isLastXferSuper = false;
                            callback('DO_BLIND_XFER_SUCCEEDED');
                        } catch (error) {
                            callback({ type: 'DO_BLIND_XFER_FAILED', message: error.message });
                        }
                    } else if (e.type === 'DO_XFER') {
                        try {
                            let body = { "request": "transfer", "uri": e.refer_to, "replace": e.replaces_call_id };
                            context.pluginHandle.send({ "message": body });
                            context.isLastXferBlind = false;
                            context.isLastXferSuper = true;
                            callback('DO_XFER_SUCCEEDED');
                        } catch (error) {
                            callback({ type: 'DO_XFER_FAILED', message: error.message });
                        }
                    }
                });
            }
        }
    }
);


function FixInactiveSdpAttr(jsep, preferSendAudio, preferRecvAudio, preferSendVideo, preferRecvVideo) {
    // the order of m-lines must be preserved or it will be rejected by PC (if order of m-lines
    // doesn't match the order of m-lines in the offer)
    if (!jsep || !jsep.sdp) return;
    if ((!preferSendAudio && !preferRecvAudio) || (!preferSendVideo && !preferRecvVideo)) {
        // find m=audio
        let maudio_index = jsep.sdp.indexOf("m=audio ");
        let mvideo_index = jsep.sdp.indexOf("m=video ");
        let maudio = '';
        let mvideo = '';
        let mindex = -1;
        if (maudio_index > -1) {
            if (mvideo_index == -1) {
                mindex = maudio_index;
                maudio = jsep.sdp.substring(maudio_index);
            }
            else if (maudio_index < mvideo_index) {
                mindex = maudio_index;
                maudio = jsep.sdp.substring(maudio_index, mvideo_index);
                mvideo = jsep.sdp.substring(mvideo_index);
            } else {
                mindex = mvideo_index;
                mvideo = jsep.sdp.substring(mvideo_index, maudio_index);
                maudio = jsep.sdp.substring(maudio_index);
            }
        } else if (mvideo_index > -1) {
            mindex = mvideo_index;
            mvideo = jsep.sdp.substring(mvideo_index);
        }

        let newSdp;
        if (mindex > -1) {
            newSdp = jsep.sdp.substring(0, mindex);
        }
        if (maudio && !preferSendAudio && !preferRecvAudio) {
            maudio = maudio.replace("a=sendonly\r\n", "a=inactive\r\n");
            maudio = maudio.replace("a=recvonly\r\n", "a=inactive\r\n");
            maudio = maudio.replace("a=sendrecv\r\n", "a=inactive\r\n");
            if (maudio.indexOf("a=inactive\r\n") < -1) maudio += "a=inactive\r\n";
        }
        if (mvideo && !preferSendVideo && !preferRecvVideo) {
            mvideo = mvideo.replace("a=sendonly\r\n", "a=inactive\r\n");
            mvideo = mvideo.replace("a=recvonly\r\n", "a=inactive\r\n");
            mvideo = mvideo.replace("a=sendrecv\r\n", "a=inactive\r\n");
            if (mvideo.indexOf("a=inactive\r\n") < -1) mvideo += "a=inactive\r\n";
        }
        if (maudio_index < mvideo_index) {
            newSdp += maudio;
            newSdp += mvideo;
        } else {
            newSdp += mvideo;
            newSdp += maudio;
        }
        jsep.sdp = newSdp;
    }
}

function calculateUserName(context, event) {
    if (context.incomingCallUserName) return context.incomingCallUserName;
    if (event.result["headers"] && event.result["headers"]["Remote-Party-Id"] && event.result["headers"]["Remote-Party-Id"].includes("sip:")) {
        let header = event.result["headers"]["Remote-Party-Id"];
        let callerId = header.indexOf("@") < 0 ? "anonymous" : header.substring(header.indexOf("sip:") + 4, header.indexOf("@"));
        if (callerId) return callerId;
    }
    return event.result["username"] !== undefined ? event.result["username"] : context.remote_uri;
}

function calculateReferredByData( event) {
    if (event.result?.referred_by) {
        const header = event.result.referred_by;
        const referredByUser = header.indexOf("@") < 0 ? undefined : header.substring(header.indexOf("sip:") + 4, header.indexOf("@"));
        let cidPos = header.indexOf(';cid="');
        if (cidPos >= 0) cidPos += 6;
        const cidPosEnd = cidPos < 0 ? -1 : header.indexOf('"', cidPos);
        const referredByCid = (cidPos < 0 || cidPosEnd < 0) ? undefined : header.substring(cidPos, cidPosEnd);
        return {referredByUser: referredByUser, referredByCid: referredByCid};
    }
    return undefined;
}


function calculateDisplayName(context, event) {
    if (context.incomingCallDisplayName) return context.incomingCallDisplayName;
    if (event.result["headers"] && event.result["headers"]["Remote-Party-Id"]) {
        let trimmed = event.result["headers"]["Remote-Party-Id"];
        if (trimmed.indexOf("\"") === 0 && trimmed.indexOf("\"", 1) > 1) {
            return trimmed.substring(1, trimmed.indexOf("\"", 1));
        } else
            return trimmed;
    } else
        return event.result["displayname"] !== undefined ? event.result["displayname"] : undefined;
}



export function setDefaultAudioConstraints(constraints) {
    defaultMediaConstraints = constraints
}


function AdjustMediaMode(context, sdp) {

    let attrContext = {
        answerSend: false,
        answerRecv: false
    };

    DetermineAnswerMediaAttr("audio", attrContext, sdp);
    context.canSendAudio = context.preferSendAudio && attrContext.answerSend;
    context.canRecvAudio = context.preferRecvAudio && attrContext.answerRecv;


    attrContext.answerSend = false;
    attrContext.answerRecv = false;
    DetermineAnswerMediaAttr("video", attrContext, sdp);
    context.canSendVideo = context.preferSendVideo && attrContext.answerSend;
    context.canRecvVideo = attrContext.answerRecv;
}

function DetermineAnswerMediaAttr(mediaType, attrContext, sdp)
{
    if (!sdp) {
        attrContext.answerSend = false;
        attrContext.answerRecv = false;
        return;
    }

    let mediaLine = "m=" + mediaType + " ";
    let mediaIndex = sdp.indexOf(mediaLine);
    let answerSend = mediaIndex > -1;
    let answerRecv = mediaIndex > -1;
    let fromIndex = mediaIndex;
    let mCount = 0;
    while (fromIndex > -1 && (mediaIndex = sdp.indexOf(mediaLine, fromIndex)) > -1)
    {
        // prefer active md's over inactives
        if (++mCount > 1 && (answerSend || answerRecv)) break;
        answerSend = true;
        answerRecv = true;
        // determine the end pos of the current md
        let end_of_md = sdp.indexOf("\r\nm=", mediaIndex + 1);
        // determine attribute index within the current md, there must be only one
        let sendOnlyModeIndex = -1;
        let recvOnlyModeIndex = -1;
        let inactiveModeIndex = -1;
        let sendrecvModeIndex = sdp.indexOf("a=sendrecv\r\n", fromIndex);
        if (sendrecvModeIndex < mediaIndex || (end_of_md > -1 && sendrecvModeIndex > end_of_md)) sendrecvModeIndex = -1;
        if (sendrecvModeIndex < 0)
        {
            sendOnlyModeIndex = sdp.indexOf("a=sendonly\r\n", fromIndex);
            if ( sendOnlyModeIndex < mediaIndex || (end_of_md > -1 && sendOnlyModeIndex > end_of_md)) sendOnlyModeIndex = -1;
            if (sendOnlyModeIndex < 0)
            {
                recvOnlyModeIndex = sdp.indexOf("a=recvonly\r\n", fromIndex);
                if (recvOnlyModeIndex < mediaIndex || (end_of_md > -1 && recvOnlyModeIndex > end_of_md)) recvOnlyModeIndex = -1;
                if (recvOnlyModeIndex < 0)
                {
                    inactiveModeIndex = sdp.indexOf("a=inactive\r\n", fromIndex);
                    if (inactiveModeIndex < mediaIndex || (end_of_md > -1 && inactiveModeIndex > end_of_md)) inactiveModeIndex = -1;
                }
            }
        }
        if (sendOnlyModeIndex > -1)
        {
            answerSend = false;
            answerRecv = true;
        }
        else if (recvOnlyModeIndex > -1)
        {
            answerSend = true;
            answerRecv = false;
        }
        else if (inactiveModeIndex > -1)
        {
            answerSend = false;
            answerRecv = false;
        }
        // advance fromIndex for the next iteration
        fromIndex = end_of_md;
    }

    attrContext.answerSend = answerSend;
    attrContext.answerRecv = answerRecv;

}


function  EnableStereoAudio(jsep) {
    // add 'sprop-stereo=1' to make sure desktop audio and microphone are seperated in channels
    if (!jsep || !jsep.sdp) return;
    if (jsep.sdp.includes('useinbandfec=')) {
        jsep.sdp = jsep.sdp.replace('useinbandfec=', 'sprop-stereo=1;useinbandfec=');
    }
}


function GetErrorMessage(event) {
    if (event.message instanceof Error) return event.message.message;
    else return event.message;
}

function HasVideoTracks(context) {
    let localTracks = context.pluginHandle.getLocalTracks();
    for (let track of localTracks) {
        if (track.type == 'video' || track.type == 'screen') return true;
    }
    let remoteTracks = context.pluginHandle.getRemoteTracks();
    for (let track of remoteTracks) {
        if (track.type == 'video' || track.type == 'screen') return true;
    }
    return undefined;
}